import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from './Toast';
import FileUploadSection from './FileUploadSection';
import SectionComponent from './SectionComponent';
import SectionQuizComponent from './SectionQuizComponent';
import { 
  useCreateSectionMutation, 
  useUploadVideoMutation, 
  useUploadPdfMutation,
  useDeleteSectionMutation,
  useDeleteVideoMutation,
  useDeletePdfMutation,
  useDeleteStandalonePdfMutation,
  useToggleSectionLockMutation,
  useUpdateCourseFeaturesMutation
} from '../redux/apiSlice';

// Vimeo Configuration with Domain Whitelist - UPDATED with wildcard
const VIMEO_ACCESS_TOKEN = "845dc90218bc132817de911c755b0f57";
const VIMEO_API_BASE_URL = "https://api.vimeo.com";
const ALLOWED_DOMAINS = [
  'pengravity.com',
  '*.pengravity.com',
  'student.pengravity.com',
  'staging.pengravity.com',
  'admin.pengravity.com',
  'localhost',
  '127.0.0.1',
  '192.168.100.7'
];

// Helper function to format duration - UPDATED for very long videos
const formatDuration = (durationInSeconds) => {
  if (!durationInSeconds || durationInSeconds <= 0) {
    return '0m';
  }
  
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = Math.floor(durationInSeconds % 60);
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// 🔐 UPDATED: Video privacy update with better error handling
const updateVideoPrivacyWithDomains = async (videoId, allowedDomains) => {
  try {
    console.log('🔒 Updating video privacy with domain whitelist:', { videoId, allowedDomains });
    
    const verifyVideo = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
      headers: {
        Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
        'Accept': 'application/vnd.vimeo.*+json;version=3.4'
      }
    });
    
    console.log('✅ Video verified:', verifyVideo.data.status);
    
    const privacyResponse = await axios.patch(
      `${VIMEO_API_BASE_URL}/videos/${videoId}`,
      {
        privacy: {
          embed: 'whitelist',
          view: 'disable'
        }
      },
      {
        headers: {
          Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
          'Content-Type': 'application/json',
          'Accept': 'application/vnd.vimeo.*+json;version=3.4'
        }
      }
    );
    
    console.log('✅ Privacy settings updated to whitelist');
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    for (const domain of allowedDomains) {
      try {
        console.log(`➕ Adding domain: ${domain}`);
        
        await axios.put(
          `${VIMEO_API_BASE_URL}/videos/${videoId}/privacy/domains/${domain}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
              'Content-Type': 'application/json',
              'Accept': 'application/vnd.vimeo.*+json;version=3.4'
            }
          }
        );
        
        console.log(`✅ Domain ${domain} added successfully`);
      } catch (domainError) {
        if (domainError.response?.status === 400) {
          console.log(`ℹ️ Domain ${domain} already whitelisted`);
        } else {
          console.warn(`⚠️ Could not add domain ${domain}:`, domainError.message);
        }
      }
    }
    
    try {
      const verifyResponse = await axios.get(
        `${VIMEO_API_BASE_URL}/videos/${videoId}`,
        {
          headers: {
            Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
            'Accept': 'application/vnd.vimeo.*+json;version=3.4'
          }
        }
      );
      
      console.log('✅ Final privacy settings:', verifyResponse.data.privacy);
    } catch (verifyError) {
      console.log('⚠️ Could not verify final settings');
    }
    
    return { success: true, message: 'Privacy settings updated successfully' };
  } catch (error) {
    console.error('❌ Failed to update video privacy:', error.response?.data || error.message);
    return { 
      success: false, 
      error: error.response?.data || error.message 
    };
  }
};

const MediaPricingStep = ({ courseData, onInputChange, onFileUpload, onArrayFieldChange, onAddArrayField, onRemoveArrayField, onThumbnailChange, refetchCourse }) => {
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [toast, setToast] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  
  const [createSection] = useCreateSectionMutation();
  const [uploadVideo] = useUploadVideoMutation();
  const [uploadPdf] = useUploadPdfMutation();
  const [deleteSection] = useDeleteSectionMutation();
  const [deleteVideo] = useDeleteVideoMutation();
  const [deletePdf] = useDeletePdfMutation();
  const [deleteStandalonePdf] = useDeleteStandalonePdfMutation();
  const [toggleSectionLock] = useToggleSectionLockMutation();
  const [updateCourseFeatures] = useUpdateCourseFeaturesMutation();

  useEffect(() => {
    console.log('📊 Course sections updated:', courseData.sections);
    
    if (courseData.sections) {
      courseData.sections.forEach((section, sIndex) => {
        console.log(`📁 Section [${sIndex}]:`, {
          name: section.name,
          serverSectionId: section.serverSectionId,
          isLock: section.isLock,
          videos: section.videos?.length || 0,
          pdfs: section.pdfs?.length || 0,
          nestedSections: section.nestedSections?.length || 0
        });
        
        if (section.nestedSections && section.nestedSections.length > 0) {
          section.nestedSections.forEach((nestedSection, nsIndex) => {
            console.log(`   ↳ Nested Section [${nsIndex}]:`, {
              name: nestedSection.name,
              serverSectionId: nestedSection.serverSectionId,
              isLock: nestedSection.isLock,
              videos: nestedSection.videos?.length || 0,
              pdfs: nestedSection.pdfs?.length || 0
            });
          });
        }
      });
    }
  }, [courseData.sections]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // ==================== PDF RENAME HELPERS ====================

  const sanitizePdfFileName = (name) => {
    if (!name) return 'document';
    return name
      .replace(/\.pdf$/i, '')
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 150) || 'document';
  };

  const buildRenamedPdfFile = (originalFile, desiredName) => {
    if (!originalFile) return null;

    const safeBaseName = sanitizePdfFileName(desiredName || originalFile.name || 'document');
    const finalName = safeBaseName.toLowerCase().endsWith('.pdf') ? safeBaseName : `${safeBaseName}.pdf`;

    try {
      return new File([originalFile], finalName, {
        type: originalFile.type || 'application/pdf',
        lastModified: Date.now()
      });
    } catch (error) {
      console.warn('⚠️ File constructor failed, using original file with custom filename in FormData:', error);
      return originalFile;
    }
  };

  const getPdfUploadFileName = (targetObject, isStandalonePdf = false) => {
    if (isStandalonePdf) {
      const standaloneTitle = targetObject?.title || targetObject?.file?.name || 'document';
      const safeBaseName = sanitizePdfFileName(standaloneTitle);
      return safeBaseName.toLowerCase().endsWith('.pdf') ? safeBaseName : `${safeBaseName}.pdf`;
    }

    const attachedPdfTitle =
      targetObject?.pdfTitle ||
      targetObject?.pdfName ||
      targetObject?.pdfFileName ||
      targetObject?.title ||
      targetObject?.pdfFile?.name ||
      'document';

    const safeBaseName = sanitizePdfFileName(attachedPdfTitle);
    return safeBaseName.toLowerCase().endsWith('.pdf') ? safeBaseName : `${safeBaseName}.pdf`;
  };

  // ==================== PDF VIEW FUNCTIONS ====================

  const handleViewPdf = (pdfUrl, pdfTitle) => {
    console.log('🔍 Opening PDF:', { pdfUrl, pdfTitle });
    
    if (!pdfUrl) {
      showToast('PDF URL not found', 'error');
      return;
    }
    
    if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      showToast(`Opening "${pdfTitle || 'PDF'}" in browser...`, 'info');
    } else if (pdfUrl.startsWith('blob:')) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      showToast(`Opening "${pdfTitle || 'PDF'}" in browser...`, 'info');
    } else if (pdfUrl.includes('google.com') || pdfUrl.includes('drive.google.com')) {
      const driveMatch = pdfUrl.match(/\/d\/([^\/]+)/);
      if (driveMatch) {
        const fileId = driveMatch[1];
        const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
        window.open(embedUrl, '_blank', 'noopener,noreferrer');
        showToast(`Opening "${pdfTitle}" in Google Drive...`, 'info');
      } else {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      showToast(`Opening PDF...`, 'info');
    }
  };

  const calculateSectionDuration = (videos) => {
    if (!videos || videos.length === 0) return '0m';
    
    let totalSeconds = 0;
    videos.forEach(video => {
      if (video.duration) {
        if (typeof video.duration === 'number') {
          totalSeconds += video.duration;
        } else if (typeof video.duration === 'string') {
          const durationStr = video.duration.toLowerCase();
          
          const hourMatch = durationStr.match(/(\d+)\s*h/);
          if (hourMatch) {
            totalSeconds += parseInt(hourMatch[1]) * 3600;
          }
          
          const minuteMatch = durationStr.match(/(\d+)\s*m/);
          if (minuteMatch) {
            totalSeconds += parseInt(minuteMatch[1]) * 60;
          }
          
          const secondMatch = durationStr.match(/(\d+)\s*s/);
          if (secondMatch) {
            totalSeconds += parseInt(secondMatch[1]);
          }
          
          if (!hourMatch && !minuteMatch && !secondMatch) {
            const seconds = parseInt(durationStr);
            if (!isNaN(seconds)) {
              totalSeconds += seconds;
            } else {
              totalSeconds += 15 * 60;
            }
          }
        }
      } else {
        totalSeconds += 15 * 60;
      }
    });
    
    return formatDuration(totalSeconds);
  };

  const handleThumbnailChange = (file, previewUrl) => {
    setThumbnailFile(file);
    if (onThumbnailChange) {
      onThumbnailChange(file, previewUrl);
    }
  };

  const createVimeoUploadTicket = async (file, title, description) => {
    try {
      const res = await axios.post(
        `${VIMEO_API_BASE_URL}/me/videos`,
        {
          upload: { 
            approach: "tus", 
            size: file.size 
          },
          name: title,
          description: description || '',
          privacy: {
            view: 'disable',
            embed: 'whitelist'
          },
          embed: {
            buttons: {
              like: false,
              share: false,
              embed: false,
              watchlater: false,
              fullscreen: true
            },
            title: {
              name: 'hide',
              owner: 'hide'
            },
            logos: {
              vimeo: false
            }
          }
        },
        {
          headers: {
            Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
            "Content-Type": "application/json",
            Accept: "application/vnd.vimeo.*+json;version=3.4",
          },
        }
      );
      
      console.log('🎫 Vimeo ticket created with privacy settings:', res.data);
      return res.data;
    } catch (err) {
      console.error("Error creating Vimeo ticket:", err.response?.data || err.message);
      throw err;
    }
  };

  const uploadToVimeo = async (file, uploadUrl, onProgress) => {
    const CHUNK_SIZE = 5 * 1024 * 1024;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    let offset = 0;

    const uploadChunk = async (chunk, chunkOffset) => {
      for (let retry = 0; retry < MAX_RETRIES; retry++) {
        try {
          const response = await fetch(uploadUrl, {
            method: "PATCH",
            headers: {
              "Tus-Resumable": "1.0.0",
              "Upload-Offset": chunkOffset.toString(),
              "Content-Type": "application/offset+octet-stream",
              Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
            },
            body: chunk,
          });

          if (!response.ok) {
            throw new Error(`Vimeo upload failed with status ${response.status}`);
          }

          const newOffset = parseInt(response.headers.get("Upload-Offset"), 10) || chunkOffset + chunk.size;
          const progress = Math.floor((newOffset / file.size) * 100);
          
          if (onProgress) {
            onProgress(progress);
          }
          
          console.log(`✅ Chunk uploaded: ${newOffset}/${file.size} bytes (${progress}%)`);
          return newOffset;
          
        } catch (error) {
          console.error(`❌ Chunk upload error (attempt ${retry + 1}/${MAX_RETRIES}):`, error);
          
          if (retry < MAX_RETRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retry + 1)));
          } else {
            throw error;
          }
        }
      }
    };

    while (offset < file.size) {
      const chunk = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size));
      
      try {
        offset = await uploadChunk(chunk, offset);
      } catch (error) {
        console.error("❌ Failed to upload chunk after all retries:", error);
        throw error;
      }
    }
    
    return 100;
  };

  const getVimeoVideoInfo = async (videoUri, retries = 60, delay = 30000) => {
    try {
      const videoId = videoUri.split('/').pop();
      console.log('🎬 Extracted Video ID for Vimeo API:', videoId);
      
      let attempt = 1;
      let totalWaitTime = 0;
      
      while (attempt <= retries) {
        try {
          const videoRes = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
            headers: { 
              Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
              'Accept': 'application/vnd.vimeo.*+json;version=3.4'
            },
          });
          
          const videoData = videoRes.data;
          const duration = videoData.duration || 0;
          
          console.log(`🎬 Vimeo API Attempt ${attempt}/${retries}:`, {
            status: videoData.status,
            duration: duration,
            transcode: videoData.transcode?.status,
            upload_status: videoData.upload?.status,
            privacy_embed: videoData.privacy?.embed,
            total_wait_time: `${Math.round(totalWaitTime/60)} minutes`
          });
          
          if (videoData.status === 'available' && 
              duration > 0 && 
              videoData.transcode?.status === 'complete') {
            
            console.log('✅ Video is fully ready! Duration:', duration, 'seconds');
            console.log(`⏱️ Total wait time: ${Math.round(totalWaitTime/60)} minutes`);
            
            try {
              console.log('🔐 Applying domain whitelist to ready video:', videoId);
              
              await new Promise(resolve => setTimeout(resolve, 5000));
              
              const privacyResult = await updateVideoPrivacyWithDomains(videoId, ALLOWED_DOMAINS);
              
              if (privacyResult.success) {
                console.log('✅ Domain whitelist applied successfully');
                
                const verifyRes = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
                  headers: { 
                    Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
                    'Accept': 'application/vnd.vimeo.*+json;version=3.4'
                  },
                });
                
                console.log('✅ Final privacy verification:', verifyRes.data.privacy);
                showToast('Video ready with domain restrictions: pengravity.com only', 'success');
              } else {
                console.warn('⚠️ Could not apply domain whitelist:', privacyResult.error);
              }
            } catch (privacyError) {
              console.error('❌ Privacy application failed:', privacyError);
            }
            
            return videoData;
          }
          
          if (videoData.upload?.status === 'complete' && 
              videoData.transcode?.status === 'in_progress') {
            
            const progress = videoData.transcode?.progress || 0;
            console.log(`⏳ Transcoding in progress: ${progress}% complete (${attempt}/${retries})`);
            
            if (duration > 21600 && attempt % 5 === 0) {
              showToast(`Processing lengthy video: ${progress}% complete. Please wait...`, 'info');
            }
          }
          
          if (attempt === retries) {
            console.log('⚠️ Max retries reached, but video may not be fully ready');
            return videoData;
          }
          
          let waitTime = delay;
          if (duration > 21600) {
            waitTime = 60000;
          } else if (duration > 7200) {
            waitTime = 45000;
          } else if (duration > 3600) {
            waitTime = 30000;
          }
          
          totalWaitTime += waitTime / 1000;
          console.log(`⏳ Waiting ${waitTime/1000} seconds... (Total: ${Math.round(totalWaitTime/60)} minutes)`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          
        } catch (attemptError) {
          console.error(`❌ Attempt ${attempt} failed:`, attemptError.message);
          
          if (attempt === retries) {
            throw attemptError;
          }
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        attempt++;
      }
      
      throw new Error('Failed to get video info after all retries');
      
    } catch (err) {
      console.error("❌ Error fetching Vimeo video info:", err.response?.data || err.message);
      throw err;
    }
  };

  // ==================== SECTION FUNCTIONS ====================

  const handleAddSection = async () => {
    if (!newSectionName.trim()) {
      showToast('Please enter a section name', 'error');
      return;
    }
    
    if (!courseData.id) {
      showToast('Please complete Step 1 first to create a course before adding sections.', 'error');
      return;
    }

    try {
      const result = await createSection({
        courseId: courseData.id,
        name: newSectionName.trim()
      }).unwrap();

      console.log('✅ Section created via API:', result);

      const newSection = {
        id: result.section.id,
        name: result.section.name,
        serverSectionId: result.section.id,
        isLock: result.section.islock !== undefined ? result.section.islock : true,
        videos: [],
        pdfs: [],
        nestedSections: [],
        deleteStatus: null
      };
      
      const updatedSections = [...(courseData.sections || []), newSection];
      onInputChange('sections', updatedSections);
      setNewSectionName('');
      setShowSectionForm(false);
      
      showToast(`Section "${newSectionName}" created successfully!`, 'success');
      
    } catch (error) {
      console.error('❌ Failed to create section:', error);
      
      let errorMessage = 'Failed to create section. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  // ==================== NESTED SECTION FUNCTIONS ====================

  const handleAddNestedSection = async (sectionIndex, nestedSectionName, isNested = false) => {
    if (!nestedSectionName.trim()) {
      showToast('Please enter a nested section name', 'error');
      return;
    }

    console.log('🔄 Adding nested section with API:', { sectionIndex, nestedSectionName, isNested });

    try {
      const parentSection = courseData.sections[sectionIndex];
      if (!parentSection?.serverSectionId) {
        showToast('Parent section ID not found. Please refresh and try again.', 'error');
        return;
      }

      const result = await createSection({
        courseId: courseData.id,
        name: nestedSectionName.trim(),
        parentId: parentSection.serverSectionId
      }).unwrap();

      console.log('✅ Nested Section (Subsection) created via API:', result);

      const newNestedSection = {
        id: result.section.id,
        name: result.section.name,
        serverSectionId: result.section.id,
        parentId: parentSection.serverSectionId,
        isLock: result.section.islock !== undefined ? result.section.islock : true,
        videos: [],
        pdfs: [],
        nestedSections: [],
        deleteStatus: null,
        isNested: true
      };
      
      const updatedSections = [...courseData.sections];
      
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: [
          ...(updatedSections[sectionIndex].nestedSections || []),
          newNestedSection
        ]
      };
      
      onInputChange('sections', updatedSections);
      showToast(`Subsection "${nestedSectionName}" created successfully!`, 'success');
      
    } catch (error) {
      console.error('❌ Failed to create nested section:', error);
      
      let errorMessage = 'Failed to create nested section. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleRemoveNestedSection = async (parentSectionIndex, nestedSectionIndex) => {
    const parentSection = courseData.sections[parentSectionIndex];
    
    if (!parentSection.nestedSections || parentSection.nestedSections.length === 0) {
      showToast('No nested sections found', 'error');
      return;
    }

    const nestedSection = parentSection.nestedSections[nestedSectionIndex];
    
    if (!nestedSection.serverSectionId) {
      showToast('Nested section ID is missing. Cannot delete subsection.', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the subsection "${nestedSection.name}"?`)) {
      return;
    }

    try {
      const updatedSections = [...courseData.sections];
      updatedSections[parentSectionIndex] = {
        ...updatedSections[parentSectionIndex],
        nestedSections: updatedSections[parentSectionIndex].nestedSections.map((section, index) => 
          index === nestedSectionIndex ? { ...section, deleteStatus: 'deleting' } : section
        )
      };
      onInputChange('sections', updatedSections);

      const result = await deleteSection(nestedSection.serverSectionId).unwrap();
      
      if (result.success) {
        showToast('Subsection deleted successfully', 'success');
        
        const finalSections = [...courseData.sections];
        finalSections[parentSectionIndex] = {
          ...finalSections[parentSectionIndex],
          nestedSections: finalSections[parentSectionIndex].nestedSections.filter(
            (_, index) => index !== nestedSectionIndex
          )
        };
        
        onInputChange('sections', finalSections);
      } else {
        throw new Error(result.message || 'Failed to delete subsection');
      }

    } catch (error) {
      console.error('❌ Failed to delete nested section:', error);
      
      const resetSections = [...courseData.sections];
      resetSections[parentSectionIndex] = {
        ...resetSections[parentSectionIndex],
        nestedSections: resetSections[parentSectionIndex].nestedSections.map((section, index) => 
          index === nestedSectionIndex ? { ...section, deleteStatus: null } : section
        )
      };
      onInputChange('sections', resetSections);
      
      let errorMessage = 'Failed to delete subsection. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  // ==================== SECTION LOCK/UNLOCK FUNCTION ====================

  const handleSectionLockToggle = async (sectionIndex, lockedValue, isNested = false, nestedSectionIndex = null) => {
    console.log('🔒 Section lock toggle called:', { sectionIndex, lockedValue, isNested, nestedSectionIndex });
    
    let section;
    if (isNested && nestedSectionIndex !== null) {
      section = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex];
    } else {
      section = courseData.sections?.[sectionIndex];
    }
    
    if (!section) {
      console.error('❌ Section not found:', { sectionIndex, isNested, nestedSectionIndex });
      showToast('Section not found', 'error');
      return;
    }
    
    if (!section.serverSectionId) {
      showToast('Section ID is missing. Cannot update lock status.', 'error');
      return;
    }

    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
          if (idx === nestedSectionIndex) {
            return {
              ...nestedSec,
              isLock: lockedValue,
              lockUpdateStatus: 'updating'
            };
          }
          return nestedSec;
        })
      };
    } else {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        isLock: lockedValue,
        lockUpdateStatus: 'updating'
      };
    }
    
    onInputChange('sections', updatedSections);
    
    try {
      const result = await toggleSectionLock(section.serverSectionId).unwrap();
      
      console.log('✅ Section lock toggled successfully:', result);
      
      const finalSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        finalSections[sectionIndex] = {
          ...finalSections[sectionIndex],
          nestedSections: finalSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
            if (idx === nestedSectionIndex) {
              return {
                ...nestedSec,
                isLock: result.data.isLock,
                lockUpdateStatus: null
              };
            }
            return nestedSec;
          })
        };
      } else {
        finalSections[sectionIndex] = {
          ...finalSections[sectionIndex],
          isLock: result.data.isLock,
          lockUpdateStatus: null
        };
      }
      
      onInputChange('sections', finalSections);
      
      showToast(
        `Section "${section.name}" is now ${result.data.isLock ? 'locked' : 'unlocked'}`, 
        'success'
      );
      
    } catch (error) {
      console.error('❌ Failed to toggle section lock:', error);
      
      const revertSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        revertSections[sectionIndex] = {
          ...revertSections[sectionIndex],
          nestedSections: revertSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
            if (idx === nestedSectionIndex) {
              return {
                ...nestedSec,
                isLock: !lockedValue,
                lockUpdateStatus: null
              };
            }
            return nestedSec;
          })
        };
      } else {
        revertSections[sectionIndex] = {
          ...revertSections[sectionIndex],
          isLock: !lockedValue,
          lockUpdateStatus: null
        };
      }
      
      onInputChange('sections', revertSections);
      
      let errorMessage = 'Failed to update section lock status. ';
      if (error.data?.message) {
        errorMessage += error.data.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  // ==================== VIDEO FUNCTIONS ====================

  const handleAddVideoToSection = (sectionIndex, file, isNested = false, nestedSectionIndex = null) => {
    const maxSize = 15 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('Video file size must be less than 15GB', 'error');
      return;
    }

    const validVideoTypes = [
      'video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm',
      'video/x-matroska', 'video/quicktime', 'video/x-msvideo'
    ];
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'flv', 'wmv'];
    
    if (!validVideoTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      showToast('Please upload a valid video file (MP4, MOV, AVI, MKV, WebM, etc.)', 'error');
      return;
    }

    const getVideoDuration = (file) => {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        
        video.onloadedmetadata = () => {
          const duration = Math.floor(video.duration) || 900;
          console.log('🎬 Extracted video duration from file:', duration, 'seconds');
          URL.revokeObjectURL(video.src);
          resolve(duration);
        };
        
        video.onerror = () => {
          console.warn('⚠️ Could not extract video duration from file');
          resolve(900);
        };
        
        video.src = URL.createObjectURL(file);
      });
    };

    getVideoDuration(file).then((durationInSeconds) => {
      const newVideo = {
        id: Date.now().toString(),
        file: file,
        url: URL.createObjectURL(file),
        title: file.name.replace(/\.[^/.]+$/, ""),
        description: '',
        duration: formatDuration(durationInSeconds),
        originalDurationSeconds: durationInSeconds,
        pdfFile: null,
        pdfTitle: '', // ✅ ADDED for rename before upload
        pdfOriginalName: null, // ✅ ADDED for rename/reference
        uploadStatus: null,
        uploadProgress: 0,
        serverVideoId: null,
        pdfUploadStatus: null,
        deleteStatus: null,
        pdfDeleteStatus: null,
        serverPdfUrl: null,
        serverPdfId: null,
        vimeoUrl: null,
        isLocked: true,
        pdfLocked: true,
        hasPdf: false
      };
      
      const updatedSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        console.log(`🎬 Adding video to NESTED section:`, {
          mainSectionIndex: sectionIndex,
          nestedSectionIndex: nestedSectionIndex,
          targetSection: updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.name
        });
        
        if (!updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]) {
          console.error('❌ Nested section not found:', { sectionIndex, nestedSectionIndex });
          showToast('Nested section not found', 'error');
          return;
        }
        
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
            if (idx === nestedSectionIndex) {
              return {
                ...nestedSection,
                videos: [...(nestedSection.videos || []), newVideo]
              };
            }
            return nestedSection;
          })
        };
        console.log(`✅ Video added to NESTED section [${sectionIndex}][${nestedSectionIndex}] (${updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.name})`);
      } else {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          videos: [...(updatedSections[sectionIndex].videos || []), newVideo]
        };
        console.log(`✅ Video added to MAIN section [${sectionIndex}] (${updatedSections[sectionIndex]?.name})`);
      }
      
      onInputChange('sections', updatedSections);
      console.log('✅ Video added with duration:', formatDuration(durationInSeconds));
      showToast(`Video "${file.name}" added to ${isNested ? 'nested ' : ''}section`, 'info');
    });
  };

  const handleRemoveVideoFromSection = (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
    const updatedSections = [...courseData.sections];
    
    console.log('🔍 Removing video:', { sectionIndex, videoIndex, isNested, nestedSectionIndex });
    
    if (isNested && nestedSectionIndex !== null) {
      if (!updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]) {
        console.error('❌ Nested section not found:', { sectionIndex, nestedSectionIndex });
        showToast('Nested section not found', 'error');
        return;
      }
      
      const nestedSection = updatedSections[sectionIndex].nestedSections[nestedSectionIndex];
      
      if (!nestedSection?.videos?.[videoIndex]) {
        console.error('❌ Video not found in nested section:', { videoIndex, nestedSection });
        showToast('Video not found in nested section', 'error');
        return;
      }
      
      const video = nestedSection.videos[videoIndex];
      
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
          if (idx === nestedSectionIndex) {
            return {
              ...nestedSec,
              videos: nestedSec.videos.filter((_, index) => index !== videoIndex)
            };
          }
          return nestedSec;
        })
      };
      
      onInputChange('sections', updatedSections);
      showToast(`Video "${video.title}" removed from nested section`, 'info');
    } else {
      if (!updatedSections[sectionIndex]?.videos?.[videoIndex]) {
        console.error('❌ Video not found in main section:', { sectionIndex, videoIndex });
        showToast('Video not found in section', 'error');
        return;
      }
      
      const video = updatedSections[sectionIndex].videos[videoIndex];
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        videos: updatedSections[sectionIndex].videos.filter((_, index) => index !== videoIndex)
      };
      
      onInputChange('sections', updatedSections);
      showToast(`Video "${video.title}" removed from section`, 'info');
    }
  };

  const handleVideoInSectionChange = (sectionIndex, videoIndex, field, value, isNested = false, nestedSectionIndex = null) => {
    console.log(`🔄 Updating video [${sectionIndex}][${videoIndex}].${field} =`, value, { isNested, nestedSectionIndex });
    
    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
          if (idx === nestedSectionIndex) {
            const updatedVideos = [...(nestedSection.videos || [])];
            if (updatedVideos[videoIndex]) {
              updatedVideos[videoIndex] = {
                ...updatedVideos[videoIndex],
                [field]: value
              };
            }
            return {
              ...nestedSection,
              videos: updatedVideos
            };
          }
          return nestedSection;
        })
      };
    } else {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        videos: [...(updatedSections[sectionIndex].videos || [])]
      };
      
      if (updatedSections[sectionIndex].videos[videoIndex]) {
        updatedSections[sectionIndex].videos[videoIndex] = {
          ...updatedSections[sectionIndex].videos[videoIndex],
          [field]: value
        };
      }
    }
    
    onInputChange('sections', updatedSections);
  };

  const handleUploadVideoToServer = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
    console.log('🎬 VIDEO UPLOAD DEBUG: Starting video upload', { 
      isNested, 
      nestedSectionIndex,
      sectionIndex 
    });
    
    let video;
    let actualSectionId;
    
    if (isNested && nestedSectionIndex !== null) {
      video = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.videos?.[videoIndex];
      actualSectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
      console.log('🔍 Nested Section Video Upload:', {
        parentSectionIndex: sectionIndex,
        nestedSectionIndex: nestedSectionIndex,
        nestedSectionId: actualSectionId,
        videoTitle: video?.title
      });
    } else {
      video = courseData.sections?.[sectionIndex]?.videos?.[videoIndex];
      actualSectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
      console.log('🔍 Main Section Video Upload:', {
        sectionIndex: sectionIndex,
        sectionId: actualSectionId,
        videoTitle: video?.title
      });
    }
    
    console.log('🎬 Video Data Before Upload:', {
      title: video?.title,
      hasFile: !!video?.file,
      serverVideoId: video?.serverVideoId,
      uploadStatus: video?.uploadStatus,
      actualSectionId: actualSectionId,
      fileSize: video?.file ? `${Math.round(video.file.size / (1024*1024*1024) * 100)/100} GB` : 'N/A',
      estimatedDuration: video?.originalDurationSeconds ? `${Math.round(video.originalDurationSeconds/3600 * 10)/10} hours` : 'N/A',
      isLocked: video?.isLocked
    });
    
    if (!video?.title?.trim()) {
      showToast('Please enter a video title before uploading.', 'error');
      return;
    }

    if (!courseData.id) {
      showToast('Course ID is missing. Please complete previous steps first.', 'error');
      return;
    }

    if (!actualSectionId) {
      showToast('Section ID is missing. Please refresh and try again.', 'error');
      return;
    }

    handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'uploading', isNested, nestedSectionIndex);
    handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 0, isNested, nestedSectionIndex);

    try {
      showToast(`Creating Vimeo upload for "${video.title}"...`, 'info');
      const ticketData = await createVimeoUploadTicket(
        video.file, 
        video.title, 
        video.description
      );
      const uploadUrl = ticketData.upload.upload_link;
      const videoUri = ticketData.uri;

      if (!uploadUrl) {
        throw new Error('No upload URL received from Vimeo');
      }

      console.log('✅ Vimeo ticket created:', {
        uploadUrl: uploadUrl.substring(0, 100) + '...',
        videoUri
      });

      const estimatedDuration = video.originalDurationSeconds || 0;
      const fileSizeGB = video.file.size / (1024 * 1024 * 1024);
      const isLongVideo = estimatedDuration > 7200 || fileSizeGB > 2;

      if (isLongVideo) {
        const hours = Math.round(estimatedDuration / 3600 * 10) / 10;
        showToast(`⏳ ${hours} hour video detected! Uploading may take 15-30 minutes. Processing will continue in background.`, 'warning');
      } else {
        showToast(`Uploading "${video.title}" to Vimeo...`, 'info');
      }
      
      let lastProgress = 0;
      const updateProgress = (progress) => {
        if (progress > lastProgress + 1 || progress === 100) {
          handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', progress, isNested, nestedSectionIndex);
          lastProgress = progress;
          
          if (isLongVideo && progress % 25 === 0) {
            showToast(`Upload progress: ${progress}% complete`, 'info');
          }
        }
      };

      await uploadToVimeo(video.file, uploadUrl, updateProgress);

      handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 100, isNested, nestedSectionIndex);
      
      let maxRetries = 15;
      let baseDelay = 5000;
      
      if (estimatedDuration > 21600) {
        maxRetries = 90;
        baseDelay = 60000;
        showToast(`Processing 6+ hour video on Vimeo... This may take 30-60 minutes. You can leave this page.`, 'warning');
      } else if (estimatedDuration > 7200) {
        maxRetries = 60;
        baseDelay = 30000;
        showToast(`Processing long video on Vimeo... This may take 15-30 minutes.`, 'info');
      } else if (estimatedDuration > 3600) {
        maxRetries = 30;
        baseDelay = 15000;
      }
      
      showToast(`Processing "${video.title}" on Vimeo... Please wait.`, 'info');
      
      const vimeoVideoInfo = await getVimeoVideoInfo(videoUri, maxRetries, baseDelay);
      const videoId = videoUri.split('/').pop();
      const vimeoUrl = `https://player.vimeo.com/video/${videoId}`;
      let durationInSeconds = vimeoVideoInfo.duration || 0;

      console.log('⏱️ Vimeo duration in seconds:', durationInSeconds);
      console.log('🔗 Vimeo URL:', vimeoUrl);
      console.log('🔐 Video will be restricted to domains:', ALLOWED_DOMAINS);

      if (!durationInSeconds || durationInSeconds === 0) {
        console.warn('⚠️ Vimeo returned 0 duration, using file metadata duration');
        durationInSeconds = video.originalDurationSeconds || 900;
        console.log('📁 Using file duration instead:', durationInSeconds, 'seconds');
      }

      const formattedDuration = formatDuration(durationInSeconds);

      showToast(`Saving "${video.title}" info to database...`, 'info');

      const vimeoResult = {
        videoUrl: vimeoUrl,
        duration: durationInSeconds,
        privacySettings: {
          view: 'disable',
          embed: 'whitelist',
          domains: ALLOWED_DOMAINS
        }
      };

      console.log('📤 Sending vimeoResult to server:', vimeoResult);
      console.log('🎯 Using Section ID for upload:', actualSectionId);
      console.log('🔒 Sending islock value:', video.isLocked);

      const result = await uploadVideo({
        courseId: courseData.id,
        sectionId: parseInt(actualSectionId, 10),
        title: video?.title,
        description: video?.description || '',
        islock: video.isLocked,
        vimeoResult: vimeoResult,
      }).unwrap();

      console.log('✅ Video uploaded to Vimeo and saved to database:', result);
      console.log('🎬 VIDEO ID RECEIVED FROM SERVER:', result.video?.id);
      console.log('🔒 Video islock from server response:', result.video?.islock);

      const updatedSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
            if (idx === nestedSectionIndex) {
              const updatedVideos = [...nestedSection.videos];
              updatedVideos[videoIndex] = {
                ...updatedVideos[videoIndex],
                serverVideoId: result.video.id,
                vimeoUrl: vimeoUrl,
                duration: formattedDuration,
                originalDurationSeconds: durationInSeconds,
                uploadStatus: 'success',
                uploadProgress: 100,
                file: null,
                url: null,
                isLocked: result.video.islock !== undefined ? result.video.islock : video.isLocked,
                privacySettings: {
                  domains: ALLOWED_DOMAINS,
                  embed: 'whitelist'
                }
              };
              return {
                ...nestedSection,
                videos: updatedVideos
              };
            }
            return nestedSection;
          })
        };
      } else {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          videos: [...updatedSections[sectionIndex].videos]
        };
        
        updatedSections[sectionIndex].videos[videoIndex] = {
          ...updatedSections[sectionIndex].videos[videoIndex],
          serverVideoId: result.video.id,
          vimeoUrl: vimeoUrl,
          duration: formattedDuration,
          originalDurationSeconds: durationInSeconds,
          uploadStatus: 'success',
          uploadProgress: 100,
          file: null,
          url: null,
          isLocked: result.video.islock !== undefined ? result.video.islock : video.isLocked,
          privacySettings: {
            domains: ALLOWED_DOMAINS,
            embed: 'whitelist'
          }
        };
      }
      
      onInputChange('sections', updatedSections);

      if (isLongVideo) {
        showToast(`✅ Long video "${video.title}" processed successfully! Ready for students.`, 'success');
      } else {
        showToast(`Video "${video.title}" uploaded to Vimeo successfully!`, 'success');
      }

    } catch (error) {
      console.error('❌ Vimeo upload failed:', error);
      
      handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'error', isNested, nestedSectionIndex);
      handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 0, isNested, nestedSectionIndex);
      
      let errorMessage = `Failed to upload video "${video.title}" to Vimeo. `;
      
      if (error.response?.status === 413) {
        errorMessage += 'File is too large. Maximum size is 2GB.';
      } else if (error.response?.status === 415) {
        errorMessage += 'Unsupported video format. Please use MP4, MOV, AVI, MKV, or WebM.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please check Vimeo token.';
      } else if (error.message?.includes('network')) {
        errorMessage += 'Network error. Please check your internet connection.';
      } else if (error.data?.message) {
        errorMessage += error.data.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  // ==================== PDF UPLOAD FUNCTIONS ====================

  const handleUploadPdfToServer = async (sectionIndex, videoIndex = null, isNested = false, nestedSectionIndex = null, isStandalonePdf = false) => {
    console.log('🔍 PDF UPLOAD DEBUG: Starting PDF upload', { 
      sectionIndex, 
      videoIndex, 
      isNested, 
      nestedSectionIndex,
      isStandalonePdf 
    });
    
    let targetObject;
    let sectionId;
    let videoId = null;
    
    if (isStandalonePdf) {
      if (isNested && nestedSectionIndex !== null) {
        targetObject = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.pdfs?.[videoIndex];
        sectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
        console.log('📄 Nested Section Standalone PDF Upload:', {
          sectionIndex: sectionIndex,
          nestedSectionIndex: nestedSectionIndex,
          pdfIndex: videoIndex,
          sectionId: sectionId,
          pdfTitle: targetObject?.title,
          hasFile: !!targetObject?.file,
          nestedSectionData: courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]
        });
      } else {
        targetObject = courseData.sections?.[sectionIndex]?.pdfs?.[videoIndex];
        sectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
        console.log('📄 Main Section Standalone PDF Upload:', {
          sectionIndex: sectionIndex,
          pdfIndex: videoIndex,
          sectionId: sectionId,
          pdfTitle: targetObject?.title,
          hasFile: !!targetObject?.file
        });
      }
    } else {
      if (isNested && nestedSectionIndex !== null) {
        targetObject = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.videos?.[videoIndex];
        sectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
      } else {
        targetObject = courseData.sections?.[sectionIndex]?.videos?.[videoIndex];
        sectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
      }
      videoId = targetObject?.serverVideoId;
      
      console.log('🎬 Video PDF Upload:', {
        videoId: videoId,
        sectionId: sectionId,
        videoTitle: targetObject?.title,
        hasPdfFile: !!targetObject?.pdfFile,
        hasFile: !!targetObject?.file,
        pdfFileExists: !!targetObject?.pdfFile,
        pdfTitle: targetObject?.pdfTitle
      });
    }
    
    console.log('📊 Target object before upload:', {
      title: targetObject?.title,
      pdfTitle: targetObject?.pdfTitle,
      hasFile: !!targetObject?.file,
      hasPdfFile: !!targetObject?.pdfFile,
      isStandalonePdf: isStandalonePdf,
      objectKeys: targetObject ? Object.keys(targetObject) : 'No object'
    });
    
    if (!targetObject) {
      showToast('PDF object not found.', 'error');
      return;
    }
    
    let fileToUpload = null;
    if (isStandalonePdf) {
      fileToUpload = targetObject?.file;
      if (!fileToUpload) {
        console.error('❌ No file found in standalone PDF:', targetObject);
        showToast('No PDF file selected for upload.', 'error');
        return;
      }
    } else {
      fileToUpload = targetObject?.pdfFile;
      if (!fileToUpload) {
        showToast('No PDF file selected for video.', 'error');
        return;
      }
    }

    const desiredPdfFileName = getPdfUploadFileName(targetObject, isStandalonePdf);
    const renamedPdfFile = buildRenamedPdfFile(fileToUpload, desiredPdfFileName);
    
    console.log('📁 File to upload:', {
      originalName: fileToUpload?.name,
      renamedName: desiredPdfFileName,
      size: fileToUpload?.size,
      type: fileToUpload?.type,
      isStandalonePdf
    });

    if (isStandalonePdf) {
      handlePdfInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'uploading', isNested, nestedSectionIndex);
    } else {
      if (isNested && nestedSectionIndex !== null) {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'uploading', isNested, nestedSectionIndex);
      } else {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'uploading', isNested);
      }
    }

    try {
      console.log('📤 Uploading PDF...', { 
        videoId: videoId, 
        sectionId: sectionId,
        isStandalonePdf: isStandalonePdf,
        fileToUpload: renamedPdfFile,
        desiredPdfFileName
      });
      
      const formData = new FormData();
      formData.append('pdf', renamedPdfFile, desiredPdfFileName);
      
      if (!isStandalonePdf && videoId) {
        formData.append('videoId', videoId.toString());
        console.log('🎯 Using videoId for PDF upload:', videoId);
      } else if (isStandalonePdf && sectionId) {
        formData.append('sectionId', sectionId.toString());
        console.log('📁 Using sectionId for standalone PDF upload:', sectionId);
      } else {
        console.error('❌ Missing ID:', { videoId, sectionId, isStandalonePdf });
        throw new Error('Either videoId or sectionId is required for PDF upload');
      }
      
      const result = await uploadPdf(formData).unwrap();

      console.log('✅ PDF upload API response:', result);

      const pdfUrl = result.pdf?.fileUrl || result.fileUrl || result.url || result.pdf?.url;
      const pdfId = result.pdf?.id || result.id;
      
      console.log('📄 Extracted PDF data:', { pdfUrl, pdfId });
      
      if (!pdfUrl) {
        console.error('❌ No PDF URL in response:', result);
        showToast('PDF uploaded but URL not found in response', 'warning');
        return;
      }

      if (isStandalonePdf) {
        console.log('🔄 Updating standalone PDF state with data...');
        
        const updatedSections = [...courseData.sections];
        
        if (isNested && nestedSectionIndex !== null) {
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
              if (idx === nestedSectionIndex) {
                const updatedPdfs = [...(nestedSection.pdfs || [])];
                if (updatedPdfs[videoIndex]) {
                  updatedPdfs[videoIndex] = {
                    ...updatedPdfs[videoIndex],
                    serverPdfUrl: pdfUrl,
                    serverPdfId: pdfId,
                    uploadStatus: 'success',
                    file: null,
                    isStandalone: true
                  };
                }
                return {
                  ...nestedSection,
                  pdfs: updatedPdfs
                };
              }
              return nestedSection;
            })
          };
        } else {
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            pdfs: updatedSections[sectionIndex].pdfs.map((pdf, idx) => {
              if (idx === videoIndex) {
                return {
                  ...pdf,
                  serverPdfUrl: pdfUrl,
                  serverPdfId: pdfId,
                  uploadStatus: 'success',
                  file: null,
                  isStandalone: true
                };
              }
              return pdf;
            })
          };
        }
        
        onInputChange('sections', updatedSections);
        showToast(`PDF "${targetObject.title}" uploaded successfully!`, 'success');
        
      } else {
        console.log('🔄 Updating video state with PDF data...');
        
        if (isNested && nestedSectionIndex !== null) {
          const updatedSections = [...courseData.sections];
          
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
              if (idx === nestedSectionIndex) {
                const updatedVideos = [...nestedSection.videos];
                if (updatedVideos[videoIndex]) {
                  updatedVideos[videoIndex] = {
                    ...updatedVideos[videoIndex],
                    serverPdfUrl: pdfUrl,
                    serverPdfId: pdfId,
                    pdfUploadStatus: 'success',
                    pdfFile: null,
                    hasPdf: true
                  };
                  console.log('✅ Updated nested section video:', updatedVideos[videoIndex]);
                }
                return {
                  ...nestedSection,
                  videos: updatedVideos
                };
              }
              return nestedSection;
            })
          };
          
          onInputChange('sections', updatedSections);
        } else {
          const updatedSections = [...courseData.sections];
          
          if (updatedSections[sectionIndex]?.videos?.[videoIndex]) {
            updatedSections[sectionIndex] = {
              ...updatedSections[sectionIndex],
              videos: [...updatedSections[sectionIndex].videos]
            };
            
            updatedSections[sectionIndex].videos[videoIndex] = {
              ...updatedSections[sectionIndex].videos[videoIndex],
              serverPdfUrl: pdfUrl,
              serverPdfId: pdfId,
              pdfUploadStatus: 'success',
              pdfFile: null,
              hasPdf: true
            };
            
            console.log('✅ Updated main section video:', updatedSections[sectionIndex].videos[videoIndex]);
          }
          
          onInputChange('sections', updatedSections);
        }

        showToast(`PDF for "${targetObject.title}" uploaded successfully!`, 'success');
      }

      setTimeout(() => {
        console.log('🔍 Final state check after PDF upload:', {
          targetTitle: targetObject.title,
          pdfUrl,
          pdfId
        });
      }, 100);

    } catch (error) {
      console.error('❌ PDF upload failed:', error);
      
      if (isStandalonePdf) {
        handlePdfInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'error', isNested, nestedSectionIndex);
      } else {
        if (isNested && nestedSectionIndex !== null) {
          handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'error', isNested, nestedSectionIndex);
        } else {
          handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'error', isNested);
        }
      }
      
      let errorMessage = 'Failed to upload PDF. ';
      
      if (error.status === 400) {
        errorMessage = 'Bad Request: Invalid video ID or PDF file format.';
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.status === 413) {
        errorMessage = 'File is too large. Maximum size is 50MB.';
      } else if (error.status === 415) {
        errorMessage = 'Unsupported file type. Please upload a PDF file.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleDeletePdf = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
    let video;
    if (isNested && nestedSectionIndex !== null) {
      video = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].videos[videoIndex];
    } else {
      video = courseData.sections[sectionIndex].videos[videoIndex];
    }
    
    if (!video.serverPdfId) {
      showToast('PDF ID is missing. Cannot delete PDF.', 'error');
      return;
    }

    if (isNested && nestedSectionIndex !== null) {
      handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', 'deleting', isNested, nestedSectionIndex);
    } else {
      handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', 'deleting', isNested);
    }

    try {
      const result = await deletePdf(video.serverPdfId).unwrap();
      
      if (result.success) {
        showToast('PDF deleted successfully', 'success');
        
        if (isNested && nestedSectionIndex !== null) {
          const updatedSections = [...courseData.sections];
          
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
              if (idx === nestedSectionIndex) {
                const updatedVideos = [...nestedSection.videos];
                if (updatedVideos[videoIndex]) {
                  updatedVideos[videoIndex] = {
                    ...updatedVideos[videoIndex],
                    pdfFile: null,
                    pdfTitle: '',
                    pdfOriginalName: null,
                    serverPdfUrl: null,
                    serverPdfId: null,
                    pdfUploadStatus: null,
                    pdfDeleteStatus: null,
                    hasPdf: false
                  };
                }
                return {
                  ...nestedSection,
                  videos: updatedVideos
                };
              }
              return nestedSection;
            })
          };
          
          onInputChange('sections', updatedSections);
        } else {
          const updatedSections = [...courseData.sections];
          
          if (updatedSections[sectionIndex]?.videos?.[videoIndex]) {
            updatedSections[sectionIndex] = {
              ...updatedSections[sectionIndex],
              videos: [...updatedSections[sectionIndex].videos]
            };
            
            updatedSections[sectionIndex].videos[videoIndex] = {
              ...updatedSections[sectionIndex].videos[videoIndex],
              pdfFile: null,
              pdfTitle: '',
              pdfOriginalName: null,
              serverPdfUrl: null,
              serverPdfId: null,
              pdfUploadStatus: null,
              pdfDeleteStatus: null,
              hasPdf: false
            };
          }
          
          onInputChange('sections', updatedSections);
        }
      } else {
        throw new Error(result.message || 'Failed to delete PDF');
      }
    } catch (error) {
      console.error('❌ Failed to delete PDF:', error);
      
      if (isNested && nestedSectionIndex !== null) {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', null, isNested, nestedSectionIndex);
      } else {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', null, isNested);
      }
      
      let errorMessage = 'Failed to delete PDF. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      }
      showToast(errorMessage, 'error');
    }
  };

  // ==================== STANDALONE PDF FUNCTIONS ====================

  const handleStandalonePdfLockToggle = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null, newLockStatus) => {
    console.log('🔒 Standalone PDF lock toggle called:', { 
      sectionIndex, 
      pdfIndex, 
      isNested, 
      nestedSectionIndex, 
      newLockStatus,
      courseId: courseData.id,
      isCreateMode: !courseData.id
    });
    
    let pdf;
    if (isNested && nestedSectionIndex !== null) {
      pdf = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.pdfs?.[pdfIndex];
    } else {
      pdf = courseData.sections?.[sectionIndex]?.pdfs?.[pdfIndex];
    }
    
    if (!pdf) {
      console.error('❌ PDF not found:', { sectionIndex, pdfIndex, isNested, nestedSectionIndex });
      showToast('PDF not found', 'error');
      return;
    }
    
    if (!pdf.serverPdfId) {
      console.log('🔒 Standalone PDF not uploaded yet, updating local state only:', newLockStatus);
      
      const updatedSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
            if (idx === nestedSectionIndex) {
              const updatedPdfs = [...(nestedSec.pdfs || [])];
              if (updatedPdfs[pdfIndex]) {
                updatedPdfs[pdfIndex] = {
                  ...updatedPdfs[pdfIndex],
                  isLocked: newLockStatus
                };
              }
              return {
                ...nestedSec,
                pdfs: updatedPdfs
              };
            }
            return nestedSec;
          })
        };
      } else {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          pdfs: updatedSections[sectionIndex].pdfs.map((p, idx) => 
            idx === pdfIndex ? { ...p, isLocked: newLockStatus } : p
          )
        };
      }
      
      onInputChange('sections', updatedSections);
      showToast(`PDF lock status set to ${newLockStatus ? 'locked' : 'unlocked'} (will apply after upload)`, 'info');
      return;
    }

    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
          if (idx === nestedSectionIndex) {
            const updatedPdfs = [...(nestedSec.pdfs || [])];
            if (updatedPdfs[pdfIndex]) {
              updatedPdfs[pdfIndex] = {
                ...updatedPdfs[pdfIndex],
                isLocked: newLockStatus,
                lockUpdateStatus: 'updating'
              };
            }
            return {
              ...nestedSec,
              pdfs: updatedPdfs
            };
          }
          return nestedSec;
        })
      };
    } else {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        pdfs: updatedSections[sectionIndex].pdfs.map((p, idx) => 
          idx === pdfIndex ? { ...p, isLocked: newLockStatus, lockUpdateStatus: 'updating' } : p
        )
      };
    }
    
    onInputChange('sections', updatedSections);
    
    try {
      console.log('🔒 Updating standalone PDF lock status via API:', {
        pdfId: pdf.serverPdfId,
        islock: newLockStatus,
        courseId: courseData.id
      });
      
      const result = await updateCourseFeatures({
        courseId: parseInt(courseData.id, 10),
        videoIds: [],
        pdfIds: [{
          id: parseInt(pdf.serverPdfId, 10),
          islock: newLockStatus
        }],
        quizIds: []
      }).unwrap();
      
      console.log('✅ Standalone PDF lock status updated successfully:', result);
      
      const finalSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        finalSections[sectionIndex] = {
          ...finalSections[sectionIndex],
          nestedSections: finalSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
            if (idx === nestedSectionIndex) {
              const updatedPdfs = [...(nestedSec.pdfs || [])];
              if (updatedPdfs[pdfIndex]) {
                updatedPdfs[pdfIndex] = {
                  ...updatedPdfs[pdfIndex],
                  isLocked: newLockStatus,
                  lockUpdateStatus: null
                };
              }
              return {
                ...nestedSec,
                pdfs: updatedPdfs
              };
            }
            return nestedSec;
          })
        };
      } else {
        finalSections[sectionIndex] = {
          ...finalSections[sectionIndex],
          pdfs: finalSections[sectionIndex].pdfs.map((p, idx) => 
            idx === pdfIndex ? { ...p, isLocked: newLockStatus, lockUpdateStatus: null } : p
          )
        };
      }
      
      onInputChange('sections', finalSections);
      
      if (refetchCourse && typeof refetchCourse === 'function') {
        try {
          await refetchCourse();
          console.log('✅ Course data refetched successfully');
        } catch (refetchError) {
          console.log('⚠️ Could not refetch course (expected in create mode):', refetchError.message);
        }
      } else {
        console.log('ℹ️ Skipping refetch - no refetchCourse function provided or not in edit mode');
      }
      
      showToast(`PDF "${pdf.title}" ${newLockStatus ? 'locked' : 'unlocked'} successfully`, 'success');
      
    } catch (error) {
      console.error('❌ Failed to update standalone PDF lock status:', error);
      
      const revertSections = [...courseData.sections];
      
      if (isNested && nestedSectionIndex !== null) {
        revertSections[sectionIndex] = {
          ...revertSections[sectionIndex],
          nestedSections: revertSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
            if (idx === nestedSectionIndex) {
              const updatedPdfs = [...(nestedSec.pdfs || [])];
              if (updatedPdfs[pdfIndex]) {
                updatedPdfs[pdfIndex] = {
                  ...updatedPdfs[pdfIndex],
                  isLocked: !newLockStatus,
                  lockUpdateStatus: null
                };
              }
              return {
                ...nestedSec,
                pdfs: updatedPdfs
              };
            }
            return nestedSec;
          })
        };
      } else {
        revertSections[sectionIndex] = {
          ...revertSections[sectionIndex],
          pdfs: revertSections[sectionIndex].pdfs.map((p, idx) => 
            idx === pdfIndex ? { ...p, isLocked: !newLockStatus, lockUpdateStatus: null } : p
          )
        };
      }
      
      onInputChange('sections', revertSections);
      
      let errorMessage = 'Failed to update PDF lock status. ';
      if (error.data?.message) {
        errorMessage += error.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleAddPdfToSection = (sectionIndex, file, isNested = false, nestedSectionIndex = null) => {
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('PDF file size must be less than 50MB', 'error');
      return;
    }

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Please upload a valid PDF file', 'error');
      return;
    }

    const basePdfTitle = file.name.replace(/\.[^/.]+$/, "");

    const newPdf = {
      id: Date.now().toString(),
      file: file,
      title: basePdfTitle,
      originalFileName: file.name, // ✅ ADDED for reference
      uploadStatus: null,
      serverPdfUrl: null,
      serverPdfId: null,
      isLocked: true,
      deleteStatus: null,
      isStandalone: true
    };
    
    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
          if (idx === nestedSectionIndex) {
            return {
              ...nestedSection,
              pdfs: [...(nestedSection.pdfs || []), newPdf]
            };
          }
          return nestedSection;
        })
      };
      console.log(`✅ PDF added to NESTED section [${sectionIndex}][${nestedSectionIndex}]`);
    } else {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        pdfs: [...(updatedSections[sectionIndex].pdfs || []), newPdf]
      };
      console.log(`✅ PDF added to MAIN section [${sectionIndex}]`);
    }
    
    onInputChange('sections', updatedSections);
    showToast(`PDF "${file.name}" added to ${isNested ? 'nested ' : ''}section`, 'info');
  };

  const handleRemovePdfFromSection = (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      const nestedSection = updatedSections[sectionIndex].nestedSections[nestedSectionIndex];
      const pdf = nestedSection.pdfs[pdfIndex];
      
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
          if (idx === nestedSectionIndex) {
            return {
              ...nestedSec,
              pdfs: nestedSec.pdfs.filter((_, index) => index !== pdfIndex)
            };
          }
          return nestedSec;
        })
      };
      
      onInputChange('sections', updatedSections);
      showToast(`PDF "${pdf.title || pdf.file?.name}" removed from nested section`, 'info');
    } else {
      const pdf = updatedSections[sectionIndex].pdfs[pdfIndex];
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        pdfs: updatedSections[sectionIndex].pdfs.filter((_, index) => index !== pdfIndex)
      };
      
      onInputChange('sections', updatedSections);
      showToast(`PDF "${pdf.title || pdf.file?.name}" removed from section`, 'info');
    }
  };

  const handlePdfInSectionChange = (sectionIndex, pdfIndex, field, value, isNested = false, nestedSectionIndex = null) => {
    console.log(`🔄 Updating PDF [${sectionIndex}][${pdfIndex}].${field} =`, value, { isNested, nestedSectionIndex });
    
    const updatedSections = [...courseData.sections];
    
    if (isNested && nestedSectionIndex !== null) {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
          if (idx === nestedSectionIndex) {
            const updatedPdfs = [...(nestedSection.pdfs || [])];
            updatedPdfs[pdfIndex] = {
              ...updatedPdfs[pdfIndex],
              [field]: value
            };
            return {
              ...nestedSection,
              pdfs: updatedPdfs
            };
          }
          return nestedSection;
        })
      };
    } else {
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        pdfs: [...(updatedSections[sectionIndex].pdfs || [])]
      };
      
      updatedSections[sectionIndex].pdfs[pdfIndex] = {
        ...updatedSections[sectionIndex].pdfs[pdfIndex],
        [field]: value
      };
    }
    
    onInputChange('sections', updatedSections);
  };

  const handleUploadStandalonePdfToServer = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
    console.log('📄 STANDALONE PDF UPLOAD DEBUG: Starting standalone PDF upload', { isNested, nestedSectionIndex });
    
    await handleUploadPdfToServer(
      sectionIndex, 
      pdfIndex, 
      isNested, 
      nestedSectionIndex, 
      true
    );
  };

  const handleDeleteStandalonePdf = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
    let pdf;
    if (isNested && nestedSectionIndex !== null) {
      pdf = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].pdfs[pdfIndex];
    } else {
      pdf = courseData.sections[sectionIndex].pdfs[pdfIndex];
    }
    
    if (!pdf.serverPdfId) {
      showToast('PDF ID is missing. Cannot delete PDF.', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the PDF "${pdf.title}"?`)) {
      return;
    }

    handlePdfInSectionChange(sectionIndex, pdfIndex, 'deleteStatus', 'deleting', isNested, nestedSectionIndex);

    try {
      console.log(`📄 Deleting standalone PDF with ID: ${pdf.serverPdfId} at /feature/pdf/${pdf.serverPdfId}`);
      
      const result = await deleteStandalonePdf(pdf.serverPdfId).unwrap();
      
      console.log('✅ Standalone PDF deleted successfully:', result);
      
      if (result.success) {
        showToast('PDF deleted successfully', 'success');
        handleRemovePdfFromSection(sectionIndex, pdfIndex, isNested, nestedSectionIndex);
      } else {
        throw new Error(result.message || 'Failed to delete PDF');
      }
    } catch (error) {
      console.error('❌ Failed to delete standalone PDF:', error);
      
      handlePdfInSectionChange(sectionIndex, pdfIndex, 'deleteStatus', null, isNested, nestedSectionIndex);
      
      let errorMessage = 'Failed to delete PDF. ';
      if (error.data?.message) {
        errorMessage += error.data.message;
      } else if (error.status === 401) {
        errorMessage += 'Authentication failed. Please log in again.';
      } else if (error.status === 403) {
        errorMessage += 'You do not have permission to delete this PDF.';
      } else if (error.status === 404) {
        errorMessage += 'PDF not found. It may have been already deleted.';
      } else if (error.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteVideo = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
    let video;
    if (isNested && nestedSectionIndex !== null) {
      video = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].videos[videoIndex];
    } else {
      video = courseData.sections[sectionIndex].videos[videoIndex];
    }
    
    if (!video.serverVideoId) {
      showToast('Video ID is missing. Cannot delete video.', 'error');
      return;
    }

    if (isNested && nestedSectionIndex !== null) {
      handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', 'deleting', isNested, nestedSectionIndex);
    } else {
      handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', 'deleting', isNested);
    }

    try {
      const result = await deleteVideo(video.serverVideoId).unwrap();
      
      if (result.success) {
        showToast('Video deleted successfully', 'success');
        handleRemoveVideoFromSection(sectionIndex, videoIndex, isNested, nestedSectionIndex);
      } else {
        throw new Error(result.message || 'Failed to delete video');
      }

    } catch (error) {
      console.error('❌ Failed to delete video:', error);
      
      if (isNested && nestedSectionIndex !== null) {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', null, isNested, nestedSectionIndex);
      } else {
        handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', null, isNested);
      }
      
      let errorMessage = 'Failed to delete video. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteSection = async (sectionIndex, isNested = false) => {
    const section = courseData.sections[sectionIndex];
    
    if (!section.serverSectionId) {
      showToast('Section ID is missing. Cannot delete section.', 'error');
      return;
    }

    const updatedSections = [...courseData.sections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      deleteStatus: 'deleting'
    };
    onInputChange('sections', updatedSections);

    try {
      const result = await deleteSection(section.serverSectionId).unwrap();
      
      if (result.success) {
        showToast('Section deleted successfully', 'success');
        const filteredSections = courseData.sections.filter((_, index) => index !== sectionIndex);
        onInputChange('sections', filteredSections);
      } else {
        throw new Error(result.message || 'Failed to delete section');
      }

    } catch (error) {
      console.error('❌ Failed to delete section:', error);
      
      const resetSections = [...courseData.sections];
      resetSections[sectionIndex] = {
        ...resetSections[sectionIndex],
        deleteStatus: null
      };
      onInputChange('sections', resetSections);
      
      let errorMessage = 'Failed to delete section. Please try again.';
      if (error.data?.message) {
        errorMessage = error.data.message;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const handleRemoveSection = (sectionIndex) => {
    const section = courseData.sections[sectionIndex];
    const filteredSections = courseData.sections.filter((_, index) => index !== sectionIndex);
    onInputChange('sections', filteredSections);
    showToast(`Section "${section.name}" removed`, 'info');
  };

  return (
    <div className="p-8 animate-fade-in">
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Media & Pricing</h2>
      
      <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <span className="font-bold">Video Security Enabled:</span> All uploaded videos are restricted to play only on <strong>pengravity.com</strong> and all its subdomains (<strong>*.pengravity.com</strong>). Videos cannot be embedded on any other website.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              ✅ Student panel, staging, and all subdomains are now supported.
            </p>
          </div>
        </div>
      </div>
      
      <div className="space-y-8">
        <FileUploadSection
          title="Course Thumbnail"
          field="thumbnail"
          description="Upload course thumbnail"
          format="Recommended: 1280x720 pixels"
          required={true}
          courseData={courseData}
          onThumbnailChange={handleThumbnailChange}
          showToast={showToast}
        />

        <div>
          <div className="flex justify-between items-center mb-6">
            <label className="block text-sm font-semibold text-gray-700">
              Course Content *
            </label>
            
            <button
              type="button"
              onClick={() => setShowSectionForm(true)}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold transition-all duration-300 hover:from-green-600 hover:to-emerald-700 hover:scale-105"
            >
              + Add Section
            </button>
          </div>

          {showSectionForm && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="Enter section name (e.g., 'Introduction', 'Advanced Topics')"
                  className="flex-1 px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddSection}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors duration-300"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSectionForm(false);
                    setNewSectionName('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {courseData.sections && courseData.sections.map((section, sectionIndex) => (
            <div key={section.id}>
              <SectionComponent
                section={section}
                sectionIndex={sectionIndex}
                handleAddVideoToSection={handleAddVideoToSection}
                handleAddPdfToSection={handleAddPdfToSection}
                handleRemoveSection={handleRemoveSection}
                handleVideoInSectionChange={handleVideoInSectionChange}
                handlePdfInSectionChange={handlePdfInSectionChange}
                calculateSectionDuration={calculateSectionDuration}
                handleUploadVideoToServer={handleUploadVideoToServer}
                handleUploadPdfToServer={handleUploadPdfToServer}
                handleUploadStandalonePdfToServer={handleUploadStandalonePdfToServer}
                handleDeleteSection={handleDeleteSection}
                handleDeleteVideo={handleDeleteVideo}
                handleDeletePdf={handleDeletePdf}
                handleDeleteStandalonePdf={handleDeleteStandalonePdf}
                handleRemoveVideoFromSection={handleRemoveVideoFromSection}
                handleRemovePdfFromSection={handleRemovePdfFromSection}
                showToast={showToast}
                handleAddNestedSection={handleAddNestedSection}
                handleRemoveNestedSection={handleRemoveNestedSection}
                handleViewPdf={handleViewPdf}
                handleSectionLockChange={handleSectionLockToggle}
                handleStandalonePdfLockChange={handleStandalonePdfLockToggle}
                handleAddQuizToSection={(sectionId, quizData) => {
                  console.log('Adding quiz to section:', { sectionId, quizData });
                  showToast('Quiz added to section (Demo Mode)', 'success');
                }}
                courseData={courseData}
                refetchCourse={refetchCourse}
              />
            </div>
          ))}

          {(!courseData.sections || courseData.sections.length === 0) && (
            <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-2xl">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-sm mb-4">No sections or content added yet.</p>
              <p className="text-xs text-gray-400">Create sections to organize your course content.</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Pricing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Course Price *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
                <input
                  type="number"
                  value={courseData.price}
                  onChange={(e) => onInputChange('price', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Promotional Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
                <input
                  type="number"
                  value={courseData.promoPrice}
                  onChange={(e) => onInputChange('promoPrice', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPricingStep;


// okokoo

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Toast from './Toast';
// import FileUploadSection from './FileUploadSection';
// import SectionComponent from './SectionComponent';
// import SectionQuizComponent from './SectionQuizComponent';
// import { 
//   useCreateSectionMutation, 
//   useUploadVideoMutation, 
//   useUploadPdfMutation,
//   useDeleteSectionMutation,
//   useDeleteVideoMutation,
//   useDeletePdfMutation,
//   useDeleteStandalonePdfMutation,
//   useToggleSectionLockMutation,
//   useUpdateCourseFeaturesMutation
// } from '../redux/apiSlice';

// // Vimeo Configuration with Domain Whitelist - UPDATED with wildcard
// const VIMEO_ACCESS_TOKEN = "845dc90218bc132817de911c755b0f57";
// const VIMEO_API_BASE_URL = "https://api.vimeo.com";
// const ALLOWED_DOMAINS = [
//   'pengravity.com',
//   '*.pengravity.com',
//   'student.pengravity.com',
//   'staging.pengravity.com',
//   'localhost',
//   '127.0.0.1',
//   '192.168.100.7'
// ];

// // Helper function to format duration - UPDATED for very long videos
// const formatDuration = (durationInSeconds) => {
//   if (!durationInSeconds || durationInSeconds <= 0) {
//     return '0m';
//   }
  
//   const hours = Math.floor(durationInSeconds / 3600);
//   const minutes = Math.floor((durationInSeconds % 3600) / 60);
//   const seconds = Math.floor(durationInSeconds % 60);
  
//   if (hours > 24) {
//     const days = Math.floor(hours / 24);
//     const remainingHours = hours % 24;
//     return `${days}d ${remainingHours}h ${minutes}m`;
//   } else if (hours > 0) {
//     return `${hours}h ${minutes}m`;
//   } else if (minutes > 0) {
//     return `${minutes}m ${seconds}s`;
//   } else {
//     return `${seconds}s`;
//   }
// };

// // 🔐 UPDATED: Video privacy update with better error handling
// const updateVideoPrivacyWithDomains = async (videoId, allowedDomains) => {
//   try {
//     console.log('🔒 Updating video privacy with domain whitelist:', { videoId, allowedDomains });
    
//     const verifyVideo = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
//       headers: {
//         Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//         'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//       }
//     });
    
//     console.log('✅ Video verified:', verifyVideo.data.status);
    
//     await axios.patch(
//       `${VIMEO_API_BASE_URL}/videos/${videoId}`,
//       {
//         privacy: {
//           embed: 'whitelist',
//           view: 'disable'
//         }
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//           'Content-Type': 'application/json',
//           'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//         }
//       }
//     );
    
//     console.log('✅ Privacy settings updated to whitelist');
    
//     await new Promise(resolve => setTimeout(resolve, 2000));
    
//     for (const domain of allowedDomains) {
//       try {
//         console.log(`➕ Adding domain: ${domain}`);
        
//         await axios.put(
//           `${VIMEO_API_BASE_URL}/videos/${videoId}/privacy/domains/${domain}`,
//           {},
//           {
//             headers: {
//               Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//               'Content-Type': 'application/json',
//               'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//             }
//           }
//         );
        
//         console.log(`✅ Domain ${domain} added successfully`);
//       } catch (domainError) {
//         if (domainError.response?.status === 400) {
//           console.log(`ℹ️ Domain ${domain} already whitelisted`);
//         } else {
//           console.warn(`⚠️ Could not add domain ${domain}:`, domainError.message);
//         }
//       }
//     }
    
//     try {
//       const verifyResponse = await axios.get(
//         `${VIMEO_API_BASE_URL}/videos/${videoId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//             'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//           }
//         }
//       );
      
//       console.log('✅ Final privacy settings:', verifyResponse.data.privacy);
//     } catch (verifyError) {
//       console.log('⚠️ Could not verify final settings');
//     }
    
//     return { success: true, message: 'Privacy settings updated successfully' };
//   } catch (error) {
//     console.error('❌ Failed to update video privacy:', error.response?.data || error.message);
//     return { 
//       success: false, 
//       error: error.response?.data || error.message 
//     };
//   }
// };

// const MediaPricingStep = ({
//   courseData,
//   onInputChange,
//   onFileUpload,
//   onArrayFieldChange,
//   onAddArrayField,
//   onRemoveArrayField,
//   onThumbnailChange,
//   refetchCourse
// }) => {
//   const [showSectionForm, setShowSectionForm] = useState(false);
//   const [newSectionName, setNewSectionName] = useState('');
//   const [toast, setToast] = useState(null);
//   const [thumbnailFile, setThumbnailFile] = useState(null);
  
//   const [createSection] = useCreateSectionMutation();
//   const [uploadVideo] = useUploadVideoMutation();
//   const [uploadPdf] = useUploadPdfMutation();
//   const [deleteSection] = useDeleteSectionMutation();
//   const [deleteVideo] = useDeleteVideoMutation();
//   const [deletePdf] = useDeletePdfMutation();
//   const [deleteStandalonePdf] = useDeleteStandalonePdfMutation();
//   const [toggleSectionLock] = useToggleSectionLockMutation();
//   const [updateCourseFeatures] = useUpdateCourseFeaturesMutation();

//   useEffect(() => {
//     console.log('📊 Course sections updated:', courseData.sections);
    
//     if (courseData.sections) {
//       courseData.sections.forEach((section, sIndex) => {
//         console.log(`📁 Section [${sIndex}]:`, {
//           name: section.name,
//           serverSectionId: section.serverSectionId,
//           isLock: section.isLock,
//           videos: section.videos?.length || 0,
//           pdfs: section.pdfs?.length || 0,
//           nestedSections: section.nestedSections?.length || 0
//         });
        
//         if (section.nestedSections && section.nestedSections.length > 0) {
//           section.nestedSections.forEach((nestedSection, nsIndex) => {
//             console.log(`   ↳ Nested Section [${nsIndex}]:`, {
//               name: nestedSection.name,
//               serverSectionId: nestedSection.serverSectionId,
//               isLock: nestedSection.isLock,
//               videos: nestedSection.videos?.length || 0,
//               pdfs: nestedSection.pdfs?.length || 0
//             });
//           });
//         }
//       });
//     }
//   }, [courseData.sections]);

//   const showToast = (message, type = 'info') => {
//     setToast({ message, type });
//     setTimeout(() => setToast(null), 5000);
//   };

//   // ✅ ADDED: sanitize file name
//   const sanitizePdfFileName = (name = 'document') => {
//     return name
//       .trim()
//       .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
//       .replace(/\s+/g, ' ')
//       .replace(/\.+$/, '')
//       .trim() || 'document';
//   };

//   // ✅ ADDED: create renamed file so backend receives changed name
//   const createRenamedPdfFile = (file, customTitle) => {
//     const safeTitle = sanitizePdfFileName(
//       customTitle || file?.name?.replace(/\.[^/.]+$/, '') || 'document'
//     );

//     const finalFileName = safeTitle.toLowerCase().endsWith('.pdf')
//       ? safeTitle
//       : `${safeTitle}.pdf`;

//     return new File([file], finalFileName, {
//       type: file.type || 'application/pdf',
//       lastModified: Date.now(),
//     });
//   };

//   // ==================== PDF VIEW FUNCTIONS ====================

//   const handleViewPdf = (pdfUrl, pdfTitle) => {
//     console.log('🔍 Opening PDF:', { pdfUrl, pdfTitle });
    
//     if (!pdfUrl) {
//       showToast('PDF URL not found', 'error');
//       return;
//     }
    
//     if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
//       window.open(pdfUrl, '_blank', 'noopener,noreferrer');
//       showToast(`Opening "${pdfTitle || 'PDF'}" in browser...`, 'info');
//     } else if (pdfUrl.startsWith('blob:')) {
//       window.open(pdfUrl, '_blank', 'noopener,noreferrer');
//       showToast(`Opening "${pdfTitle || 'PDF'}" in browser...`, 'info');
//     } else if (pdfUrl.includes('google.com') || pdfUrl.includes('drive.google.com')) {
//       const driveMatch = pdfUrl.match(/\/d\/([^\/]+)/);
//       if (driveMatch) {
//         const fileId = driveMatch[1];
//         const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
//         window.open(embedUrl, '_blank', 'noopener,noreferrer');
//         showToast(`Opening "${pdfTitle}" in Google Drive...`, 'info');
//       } else {
//         window.open(pdfUrl, '_blank', 'noopener,noreferrer');
//       }
//     } else {
//       window.open(pdfUrl, '_blank', 'noopener,noreferrer');
//       showToast(`Opening PDF...`, 'info');
//     }
//   };

//   const calculateSectionDuration = (videos) => {
//     if (!videos || videos.length === 0) return '0m';
    
//     let totalSeconds = 0;
//     videos.forEach(video => {
//       if (video.duration) {
//         if (typeof video.duration === 'number') {
//           totalSeconds += video.duration;
//         } else if (typeof video.duration === 'string') {
//           const durationStr = video.duration.toLowerCase();
          
//           const hourMatch = durationStr.match(/(\d+)\s*h/);
//           if (hourMatch) {
//             totalSeconds += parseInt(hourMatch[1]) * 3600;
//           }
          
//           const minuteMatch = durationStr.match(/(\d+)\s*m/);
//           if (minuteMatch) {
//             totalSeconds += parseInt(minuteMatch[1]) * 60;
//           }
          
//           const secondMatch = durationStr.match(/(\d+)\s*s/);
//           if (secondMatch) {
//             totalSeconds += parseInt(secondMatch[1]);
//           }
          
//           if (!hourMatch && !minuteMatch && !secondMatch) {
//             const seconds = parseInt(durationStr);
//             if (!isNaN(seconds)) {
//               totalSeconds += seconds;
//             } else {
//               totalSeconds += 15 * 60;
//             }
//           }
//         }
//       } else {
//         totalSeconds += 15 * 60;
//       }
//     });
    
//     return formatDuration(totalSeconds);
//   };

//   const handleThumbnailChange = (file, previewUrl) => {
//     setThumbnailFile(file);
//     if (onThumbnailChange) {
//       onThumbnailChange(file, previewUrl);
//     }
//   };

//   const createVimeoUploadTicket = async (file, title, description) => {
//     try {
//       const res = await axios.post(
//         `${VIMEO_API_BASE_URL}/me/videos`,
//         {
//           upload: { 
//             approach: "tus", 
//             size: file.size 
//           },
//           name: title,
//           description: description || '',
//           privacy: {
//             view: 'disable',
//             embed: 'whitelist'
//           },
//           embed: {
//             buttons: {
//               like: false,
//               share: false,
//               embed: false,
//               watchlater: false,
//               fullscreen: true
//             },
//             title: {
//               name: 'hide',
//               owner: 'hide'
//             },
//             logos: {
//               vimeo: false
//             }
//           }
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//             "Content-Type": "application/json",
//             Accept: "application/vnd.vimeo.*+json;version=3.4",
//           },
//         }
//       );
      
//       console.log('🎫 Vimeo ticket created with privacy settings:', res.data);
//       return res.data;
//     } catch (err) {
//       console.error("Error creating Vimeo ticket:", err.response?.data || err.message);
//       throw err;
//     }
//   };

//   const uploadToVimeo = async (file, uploadUrl, onProgress) => {
//     const CHUNK_SIZE = 5 * 1024 * 1024;
//     const MAX_RETRIES = 3;
//     const RETRY_DELAY = 1000;

//     let offset = 0;

//     const uploadChunk = async (chunk, chunkOffset) => {
//       for (let retry = 0; retry < MAX_RETRIES; retry++) {
//         try {
//           const response = await fetch(uploadUrl, {
//             method: "PATCH",
//             headers: {
//               "Tus-Resumable": "1.0.0",
//               "Upload-Offset": chunkOffset.toString(),
//               "Content-Type": "application/offset+octet-stream",
//               Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//             },
//             body: chunk,
//           });

//           if (!response.ok) {
//             throw new Error(`Vimeo upload failed with status ${response.status}`);
//           }

//           const newOffset = parseInt(response.headers.get("Upload-Offset"), 10) || chunkOffset + chunk.size;
//           const progress = Math.floor((newOffset / file.size) * 100);
          
//           if (onProgress) {
//             onProgress(progress);
//           }
          
//           console.log(`✅ Chunk uploaded: ${newOffset}/${file.size} bytes (${progress}%)`);
//           return newOffset;
          
//         } catch (error) {
//           console.error(`❌ Chunk upload error (attempt ${retry + 1}/${MAX_RETRIES}):`, error);
          
//           if (retry < MAX_RETRIES - 1) {
//             await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retry + 1)));
//           } else {
//             throw error;
//           }
//         }
//       }
//     };

//     while (offset < file.size) {
//       const chunk = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size));
      
//       try {
//         offset = await uploadChunk(chunk, offset);
//       } catch (error) {
//         console.error("❌ Failed to upload chunk after all retries:", error);
//         throw error;
//       }
//     }
    
//     return 100;
//   };

//   const getVimeoVideoInfo = async (videoUri, retries = 60, delay = 30000) => {
//     try {
//       const videoId = videoUri.split('/').pop();
//       console.log('🎬 Extracted Video ID for Vimeo API:', videoId);
      
//       let attempt = 1;
//       let totalWaitTime = 0;
      
//       while (attempt <= retries) {
//         try {
//           const videoRes = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
//             headers: { 
//               Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//               'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//             },
//           });
          
//           const videoData = videoRes.data;
//           const duration = videoData.duration || 0;
          
//           console.log(`🎬 Vimeo API Attempt ${attempt}/${retries}:`, {
//             status: videoData.status,
//             duration: duration,
//             transcode: videoData.transcode?.status,
//             upload_status: videoData.upload?.status,
//             privacy_embed: videoData.privacy?.embed,
//             total_wait_time: `${Math.round(totalWaitTime/60)} minutes`
//           });
          
//           if (videoData.status === 'available' && 
//               duration > 0 && 
//               videoData.transcode?.status === 'complete') {
            
//             console.log('✅ Video is fully ready! Duration:', duration, 'seconds');
//             console.log(`⏱️ Total wait time: ${Math.round(totalWaitTime/60)} minutes`);
            
//             try {
//               console.log('🔐 Applying domain whitelist to ready video:', videoId);
//               await new Promise(resolve => setTimeout(resolve, 5000));
              
//               const privacyResult = await updateVideoPrivacyWithDomains(videoId, ALLOWED_DOMAINS);
              
//               if (privacyResult.success) {
//                 console.log('✅ Domain whitelist applied successfully');
                
//                 const verifyRes = await axios.get(`${VIMEO_API_BASE_URL}/videos/${videoId}`, {
//                   headers: { 
//                     Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}`,
//                     'Accept': 'application/vnd.vimeo.*+json;version=3.4'
//                   },
//                 });
                
//                 console.log('✅ Final privacy verification:', verifyRes.data.privacy);
//                 showToast('Video ready with domain restrictions: pengravity.com only', 'success');
//               } else {
//                 console.warn('⚠️ Could not apply domain whitelist:', privacyResult.error);
//               }
//             } catch (privacyError) {
//               console.error('❌ Privacy application failed:', privacyError);
//             }
            
//             return videoData;
//           }
          
//           if (videoData.upload?.status === 'complete' && 
//               videoData.transcode?.status === 'in_progress') {
            
//             const progress = videoData.transcode?.progress || 0;
//             console.log(`⏳ Transcoding in progress: ${progress}% complete (${attempt}/${retries})`);
            
//             if (duration > 21600 && attempt % 5 === 0) {
//               showToast(`Processing lengthy video: ${progress}% complete. Please wait...`, 'info');
//             }
//           }
          
//           if (attempt === retries) {
//             console.log('⚠️ Max retries reached, but video may not be fully ready');
//             return videoData;
//           }
          
//           let waitTime = delay;
//           if (duration > 21600) {
//             waitTime = 60000;
//           } else if (duration > 7200) {
//             waitTime = 45000;
//           } else if (duration > 3600) {
//             waitTime = 30000;
//           }
          
//           totalWaitTime += waitTime / 1000;
//           console.log(`⏳ Waiting ${waitTime/1000} seconds... (Total: ${Math.round(totalWaitTime/60)} minutes)`);
//           await new Promise(resolve => setTimeout(resolve, waitTime));
          
//         } catch (attemptError) {
//           console.error(`❌ Attempt ${attempt} failed:`, attemptError.message);
          
//           if (attempt === retries) {
//             throw attemptError;
//           }
          
//           await new Promise(resolve => setTimeout(resolve, delay));
//         }
        
//         attempt++;
//       }
      
//       throw new Error('Failed to get video info after all retries');
      
//     } catch (err) {
//       console.error("❌ Error fetching Vimeo video info:", err.response?.data || err.message);
//       throw err;
//     }
//   };

//   // ==================== SECTION FUNCTIONS ====================

//   const handleAddSection = async () => {
//     if (!newSectionName.trim()) {
//       showToast('Please enter a section name', 'error');
//       return;
//     }
    
//     if (!courseData.id) {
//       showToast('Please complete Step 1 first to create a course before adding sections.', 'error');
//       return;
//     }

//     try {
//       const result = await createSection({
//         courseId: courseData.id,
//         name: newSectionName.trim()
//       }).unwrap();

//       console.log('✅ Section created via API:', result);

//       const newSection = {
//         id: result.section.id,
//         name: result.section.name,
//         serverSectionId: result.section.id,
//         isLock: result.section.islock !== undefined ? result.section.islock : true,
//         videos: [],
//         pdfs: [],
//         nestedSections: [],
//         deleteStatus: null
//       };
      
//       const updatedSections = [...(courseData.sections || []), newSection];
//       onInputChange('sections', updatedSections);
//       setNewSectionName('');
//       setShowSectionForm(false);
      
//       showToast(`Section "${newSectionName}" created successfully!`, 'success');
      
//     } catch (error) {
//       console.error('❌ Failed to create section:', error);
      
//       let errorMessage = 'Failed to create section. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       } else if (error.status === 401) {
//         errorMessage = 'Authentication failed. Please log in again.';
//       } else if (error.status === 500) {
//         errorMessage = 'Server error. Please try again later.';
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleAddNestedSection = async (sectionIndex, nestedSectionName, isNested = false) => {
//     if (!nestedSectionName.trim()) {
//       showToast('Please enter a nested section name', 'error');
//       return;
//     }

//     console.log('🔄 Adding nested section with API:', { sectionIndex, nestedSectionName, isNested });

//     try {
//       const parentSection = courseData.sections[sectionIndex];
//       if (!parentSection?.serverSectionId) {
//         showToast('Parent section ID not found. Please refresh and try again.', 'error');
//         return;
//       }

//       const result = await createSection({
//         courseId: courseData.id,
//         name: nestedSectionName.trim(),
//         parentId: parentSection.serverSectionId
//       }).unwrap();

//       console.log('✅ Nested Section (Subsection) created via API:', result);

//       const newNestedSection = {
//         id: result.section.id,
//         name: result.section.name,
//         serverSectionId: result.section.id,
//         parentId: parentSection.serverSectionId,
//         isLock: result.section.islock !== undefined ? result.section.islock : true,
//         videos: [],
//         pdfs: [],
//         nestedSections: [],
//         deleteStatus: null,
//         isNested: true
//       };
      
//       const updatedSections = [...courseData.sections];
      
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: [
//           ...(updatedSections[sectionIndex].nestedSections || []),
//           newNestedSection
//         ]
//       };
      
//       onInputChange('sections', updatedSections);
//       showToast(`Subsection "${nestedSectionName}" created successfully!`, 'success');
      
//     } catch (error) {
//       console.error('❌ Failed to create nested section:', error);
      
//       let errorMessage = 'Failed to create nested section. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       } else if (error.status === 401) {
//         errorMessage = 'Authentication failed. Please log in again.';
//       } else if (error.status === 500) {
//         errorMessage = 'Server error. Please try again later.';
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleRemoveNestedSection = async (parentSectionIndex, nestedSectionIndex) => {
//     const parentSection = courseData.sections[parentSectionIndex];
    
//     if (!parentSection.nestedSections || parentSection.nestedSections.length === 0) {
//       showToast('No nested sections found', 'error');
//       return;
//     }

//     const nestedSection = parentSection.nestedSections[nestedSectionIndex];
    
//     if (!nestedSection.serverSectionId) {
//       showToast('Nested section ID is missing. Cannot delete subsection.', 'error');
//       return;
//     }

//     if (!window.confirm(`Are you sure you want to delete the subsection "${nestedSection.name}"?`)) {
//       return;
//     }

//     try {
//       const updatedSections = [...courseData.sections];
//       updatedSections[parentSectionIndex] = {
//         ...updatedSections[parentSectionIndex],
//         nestedSections: updatedSections[parentSectionIndex].nestedSections.map((section, index) => 
//           index === nestedSectionIndex ? { ...section, deleteStatus: 'deleting' } : section
//         )
//       };
//       onInputChange('sections', updatedSections);

//       const result = await deleteSection(nestedSection.serverSectionId).unwrap();
      
//       if (result.success) {
//         showToast('Subsection deleted successfully', 'success');
        
//         const finalSections = [...courseData.sections];
//         finalSections[parentSectionIndex] = {
//           ...finalSections[parentSectionIndex],
//           nestedSections: finalSections[parentSectionIndex].nestedSections.filter(
//             (_, index) => index !== nestedSectionIndex
//           )
//         };
        
//         onInputChange('sections', finalSections);
//       } else {
//         throw new Error(result.message || 'Failed to delete subsection');
//       }

//     } catch (error) {
//       console.error('❌ Failed to delete nested section:', error);
      
//       const resetSections = [...courseData.sections];
//       resetSections[parentSectionIndex] = {
//         ...resetSections[parentSectionIndex],
//         nestedSections: resetSections[parentSectionIndex].nestedSections.map((section, index) => 
//           index === nestedSectionIndex ? { ...section, deleteStatus: null } : section
//         )
//       };
//       onInputChange('sections', resetSections);
      
//       let errorMessage = 'Failed to delete subsection. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleSectionLockToggle = async (sectionIndex, lockedValue, isNested = false, nestedSectionIndex = null) => {
//     console.log('🔒 Section lock toggle called:', { sectionIndex, lockedValue, isNested, nestedSectionIndex });
    
//     let section;
//     if (isNested && nestedSectionIndex !== null) {
//       section = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex];
//     } else {
//       section = courseData.sections?.[sectionIndex];
//     }
    
//     if (!section) {
//       console.error('❌ Section not found:', { sectionIndex, isNested, nestedSectionIndex });
//       showToast('Section not found', 'error');
//       return;
//     }
    
//     if (!section.serverSectionId) {
//       showToast('Section ID is missing. Cannot update lock status.', 'error');
//       return;
//     }

//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//           if (idx === nestedSectionIndex) {
//             return {
//               ...nestedSec,
//               isLock: lockedValue,
//               lockUpdateStatus: 'updating'
//             };
//           }
//           return nestedSec;
//         })
//       };
//     } else {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         isLock: lockedValue,
//         lockUpdateStatus: 'updating'
//       };
//     }
    
//     onInputChange('sections', updatedSections);
    
//     try {
//       const result = await toggleSectionLock(section.serverSectionId).unwrap();
      
//       console.log('✅ Section lock toggled successfully:', result);
      
//       const finalSections = [...courseData.sections];
      
//       if (isNested && nestedSectionIndex !== null) {
//         finalSections[sectionIndex] = {
//           ...finalSections[sectionIndex],
//           nestedSections: finalSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//             if (idx === nestedSectionIndex) {
//               return {
//                 ...nestedSec,
//                 isLock: result.data.isLock,
//                 lockUpdateStatus: null
//               };
//             }
//             return nestedSec;
//           })
//         };
//       } else {
//         finalSections[sectionIndex] = {
//           ...finalSections[sectionIndex],
//           isLock: result.data.isLock,
//           lockUpdateStatus: null
//         };
//       }
      
//       onInputChange('sections', finalSections);
      
//       showToast(
//         `Section "${section.name}" is now ${result.data.isLock ? 'locked' : 'unlocked'}`, 
//         'success'
//       );
      
//     } catch (error) {
//       console.error('❌ Failed to toggle section lock:', error);
      
//       const revertSections = [...courseData.sections];
      
//       if (isNested && nestedSectionIndex !== null) {
//         revertSections[sectionIndex] = {
//           ...revertSections[sectionIndex],
//           nestedSections: revertSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//             if (idx === nestedSectionIndex) {
//               return {
//                 ...nestedSec,
//                 isLock: !lockedValue,
//                 lockUpdateStatus: null
//               };
//             }
//             return nestedSec;
//           })
//         };
//       } else {
//         revertSections[sectionIndex] = {
//           ...revertSections[sectionIndex],
//           isLock: !lockedValue,
//           lockUpdateStatus: null
//         };
//       }
      
//       onInputChange('sections', revertSections);
      
//       let errorMessage = 'Failed to update section lock status. ';
//       if (error.data?.message) {
//         errorMessage += error.data.message;
//       } else {
//         errorMessage += 'Please try again.';
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   // ==================== VIDEO FUNCTIONS ====================

//   const handleAddVideoToSection = (sectionIndex, file, isNested = false, nestedSectionIndex = null) => {
//     const maxSize = 15 * 1024 * 1024 * 1024;
//     if (file.size > maxSize) {
//       showToast('Video file size must be less than 15GB', 'error');
//       return;
//     }

//     const validVideoTypes = [
//       'video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm',
//       'video/x-matroska', 'video/quicktime', 'video/x-msvideo'
//     ];
    
//     const fileExtension = file.name.split('.').pop().toLowerCase();
//     const allowedExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'flv', 'wmv'];
    
//     if (!validVideoTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
//       showToast('Please upload a valid video file (MP4, MOV, AVI, MKV, WebM, etc.)', 'error');
//       return;
//     }

//     const getVideoDuration = (file) => {
//       return new Promise((resolve) => {
//         const video = document.createElement('video');
//         video.preload = 'metadata';
        
//         video.onloadedmetadata = () => {
//           const duration = Math.floor(video.duration) || 900;
//           console.log('🎬 Extracted video duration from file:', duration, 'seconds');
//           URL.revokeObjectURL(video.src);
//           resolve(duration);
//         };
        
//         video.onerror = () => {
//           console.warn('⚠️ Could not extract video duration from file');
//           resolve(900);
//         };
        
//         video.src = URL.createObjectURL(file);
//       });
//     };

//     getVideoDuration(file).then((durationInSeconds) => {
//       const newVideo = {
//         id: Date.now().toString(),
//         file: file,
//         url: URL.createObjectURL(file),
//         title: file.name.replace(/\.[^/.]+$/, ""),
//         description: '',
//         duration: formatDuration(durationInSeconds),
//         originalDurationSeconds: durationInSeconds,
//         pdfFile: null,
//         uploadStatus: null,
//         uploadProgress: 0,
//         serverVideoId: null,
//         pdfUploadStatus: null,
//         deleteStatus: null,
//         pdfDeleteStatus: null,
//         serverPdfUrl: null,
//         serverPdfId: null,
//         vimeoUrl: null,
//         isLocked: true,
//         pdfLocked: true,
//         hasPdf: false
//       };
      
//       const updatedSections = [...courseData.sections];
      
//       if (isNested && nestedSectionIndex !== null) {
//         if (!updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]) {
//           console.error('❌ Nested section not found:', { sectionIndex, nestedSectionIndex });
//           showToast('Nested section not found', 'error');
//           return;
//         }
        
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//             if (idx === nestedSectionIndex) {
//               return {
//                 ...nestedSection,
//                 videos: [...(nestedSection.videos || []), newVideo]
//               };
//             }
//             return nestedSection;
//           })
//         };
//       } else {
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           videos: [...(updatedSections[sectionIndex].videos || []), newVideo]
//         };
//       }
      
//       onInputChange('sections', updatedSections);
//       showToast(`Video "${file.name}" added to ${isNested ? 'nested ' : ''}section`, 'info');
//     });
//   };

//   const handleRemoveVideoFromSection = (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       if (!updatedSections[sectionIndex]?.nestedSections?.[nestedSectionIndex]) {
//         showToast('Nested section not found', 'error');
//         return;
//       }
      
//       const nestedSection = updatedSections[sectionIndex].nestedSections[nestedSectionIndex];
      
//       if (!nestedSection?.videos?.[videoIndex]) {
//         showToast('Video not found in nested section', 'error');
//         return;
//       }
      
//       const video = nestedSection.videos[videoIndex];
      
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//           if (idx === nestedSectionIndex) {
//             return {
//               ...nestedSec,
//               videos: nestedSec.videos.filter((_, index) => index !== videoIndex)
//             };
//           }
//           return nestedSec;
//         })
//       };
      
//       onInputChange('sections', updatedSections);
//       showToast(`Video "${video.title}" removed from nested section`, 'info');
//     } else {
//       if (!updatedSections[sectionIndex]?.videos?.[videoIndex]) {
//         showToast('Video not found in section', 'error');
//         return;
//       }
      
//       const video = updatedSections[sectionIndex].videos[videoIndex];
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         videos: updatedSections[sectionIndex].videos.filter((_, index) => index !== videoIndex)
//       };
      
//       onInputChange('sections', updatedSections);
//       showToast(`Video "${video.title}" removed from section`, 'info');
//     }
//   };

//   const handleVideoInSectionChange = (sectionIndex, videoIndex, field, value, isNested = false, nestedSectionIndex = null) => {
//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//           if (idx === nestedSectionIndex) {
//             const updatedVideos = [...(nestedSection.videos || [])];
//             if (updatedVideos[videoIndex]) {
//               updatedVideos[videoIndex] = {
//                 ...updatedVideos[videoIndex],
//                 [field]: value
//               };
//             }
//             return {
//               ...nestedSection,
//               videos: updatedVideos
//             };
//           }
//           return nestedSection;
//         })
//       };
//     } else {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         videos: [...(updatedSections[sectionIndex].videos || [])]
//       };
      
//       if (updatedSections[sectionIndex].videos[videoIndex]) {
//         updatedSections[sectionIndex].videos[videoIndex] = {
//           ...updatedSections[sectionIndex].videos[videoIndex],
//           [field]: value
//         };
//       }
//     }
    
//     onInputChange('sections', updatedSections);
//   };

//   const handleUploadVideoToServer = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
//     let video;
//     let actualSectionId;
    
//     if (isNested && nestedSectionIndex !== null) {
//       video = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.videos?.[videoIndex];
//       actualSectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
//     } else {
//       video = courseData.sections?.[sectionIndex]?.videos?.[videoIndex];
//       actualSectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
//     }
    
//     if (!video?.title?.trim()) {
//       showToast('Please enter a video title before uploading.', 'error');
//       return;
//     }

//     if (!courseData.id) {
//       showToast('Course ID is missing. Please complete previous steps first.', 'error');
//       return;
//     }

//     if (!actualSectionId) {
//       showToast('Section ID is missing. Please refresh and try again.', 'error');
//       return;
//     }

//     handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'uploading', isNested, nestedSectionIndex);
//     handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 0, isNested, nestedSectionIndex);

//     try {
//       showToast(`Creating Vimeo upload for "${video.title}"...`, 'info');
//       const ticketData = await createVimeoUploadTicket(
//         video.file, 
//         video.title, 
//         video.description
//       );
//       const uploadUrl = ticketData.upload.upload_link;
//       const videoUri = ticketData.uri;

//       if (!uploadUrl) {
//         throw new Error('No upload URL received from Vimeo');
//       }

//       const estimatedDuration = video.originalDurationSeconds || 0;
//       const fileSizeGB = video.file.size / (1024 * 1024 * 1024);
//       const isLongVideo = estimatedDuration > 7200 || fileSizeGB > 2;

//       if (isLongVideo) {
//         const hours = Math.round(estimatedDuration / 3600 * 10) / 10;
//         showToast(`⏳ ${hours} hour video detected! Uploading may take 15-30 minutes. Processing will continue in background.`, 'warning');
//       } else {
//         showToast(`Uploading "${video.title}" to Vimeo...`, 'info');
//       }
      
//       let lastProgress = 0;
//       const updateProgress = (progress) => {
//         if (progress > lastProgress + 1 || progress === 100) {
//           handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', progress, isNested, nestedSectionIndex);
//           lastProgress = progress;
          
//           if (isLongVideo && progress % 25 === 0) {
//             showToast(`Upload progress: ${progress}% complete`, 'info');
//           }
//         }
//       };

//       await uploadToVimeo(video.file, uploadUrl, updateProgress);

//       handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 100, isNested, nestedSectionIndex);
      
//       let maxRetries = 15;
//       let baseDelay = 5000;
      
//       if (estimatedDuration > 21600) {
//         maxRetries = 90;
//         baseDelay = 60000;
//         showToast(`Processing 6+ hour video on Vimeo... This may take 30-60 minutes. You can leave this page.`, 'warning');
//       } else if (estimatedDuration > 7200) {
//         maxRetries = 60;
//         baseDelay = 30000;
//         showToast(`Processing long video on Vimeo... This may take 15-30 minutes.`, 'info');
//       } else if (estimatedDuration > 3600) {
//         maxRetries = 30;
//         baseDelay = 15000;
//       }
      
//       showToast(`Processing "${video.title}" on Vimeo... Please wait.`, 'info');
      
//       const vimeoVideoInfo = await getVimeoVideoInfo(videoUri, maxRetries, baseDelay);
//       const videoId = videoUri.split('/').pop();
//       const vimeoUrl = `https://player.vimeo.com/video/${videoId}`;
//       let durationInSeconds = vimeoVideoInfo.duration || 0;

//       if (!durationInSeconds || durationInSeconds === 0) {
//         durationInSeconds = video.originalDurationSeconds || 900;
//       }

//       const formattedDuration = formatDuration(durationInSeconds);

//       showToast(`Saving "${video.title}" info to database...`, 'info');

//       const vimeoResult = {
//         videoUrl: vimeoUrl,
//         duration: durationInSeconds,
//         privacySettings: {
//           view: 'disable',
//           embed: 'whitelist',
//           domains: ALLOWED_DOMAINS
//         }
//       };

//       const result = await uploadVideo({
//         courseId: courseData.id,
//         sectionId: parseInt(actualSectionId, 10),
//         title: video?.title,
//         description: video?.description || '',
//         islock: video.isLocked,
//         vimeoResult: vimeoResult,
//       }).unwrap();

//       const updatedSections = [...courseData.sections];
      
//       if (isNested && nestedSectionIndex !== null) {
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//             if (idx === nestedSectionIndex) {
//               const updatedVideos = [...nestedSection.videos];
//               updatedVideos[videoIndex] = {
//                 ...updatedVideos[videoIndex],
//                 serverVideoId: result.video.id,
//                 vimeoUrl: vimeoUrl,
//                 duration: formattedDuration,
//                 originalDurationSeconds: durationInSeconds,
//                 uploadStatus: 'success',
//                 uploadProgress: 100,
//                 file: null,
//                 url: null,
//                 isLocked: result.video.islock !== undefined ? result.video.islock : video.isLocked,
//                 privacySettings: {
//                   domains: ALLOWED_DOMAINS,
//                   embed: 'whitelist'
//                 }
//               };
//               return {
//                 ...nestedSection,
//                 videos: updatedVideos
//               };
//             }
//             return nestedSection;
//           })
//         };
//       } else {
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           videos: [...updatedSections[sectionIndex].videos]
//         };
        
//         updatedSections[sectionIndex].videos[videoIndex] = {
//           ...updatedSections[sectionIndex].videos[videoIndex],
//           serverVideoId: result.video.id,
//           vimeoUrl: vimeoUrl,
//           duration: formattedDuration,
//           originalDurationSeconds: durationInSeconds,
//           uploadStatus: 'success',
//           uploadProgress: 100,
//           file: null,
//           url: null,
//           isLocked: result.video.islock !== undefined ? result.video.islock : video.isLocked,
//           privacySettings: {
//             domains: ALLOWED_DOMAINS,
//             embed: 'whitelist'
//           }
//         };
//       }
      
//       onInputChange('sections', updatedSections);

//       if (isLongVideo) {
//         showToast(`✅ Long video "${video.title}" processed successfully! Ready for students.`, 'success');
//       } else {
//         showToast(`Video "${video.title}" uploaded to Vimeo successfully!`, 'success');
//       }

//     } catch (error) {
//       console.error('❌ Vimeo upload failed:', error);
      
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'error', isNested, nestedSectionIndex);
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'uploadProgress', 0, isNested, nestedSectionIndex);
      
//       let errorMessage = `Failed to upload video "${video.title}" to Vimeo. `;
      
//       if (error.response?.status === 413) {
//         errorMessage += 'File is too large. Maximum size is 2GB.';
//       } else if (error.response?.status === 415) {
//         errorMessage += 'Unsupported video format. Please use MP4, MOV, AVI, MKV, or WebM.';
//       } else if (error.response?.status === 401) {
//         errorMessage += 'Authentication failed. Please check Vimeo token.';
//       } else if (error.message?.includes('network')) {
//         errorMessage += 'Network error. Please check your internet connection.';
//       } else if (error.data?.message) {
//         errorMessage += error.data.message;
//       } else {
//         errorMessage += 'Please try again.';
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   // ==================== PDF UPLOAD FUNCTIONS ====================

//   const handleUploadPdfToServer = async (sectionIndex, videoIndex = null, isNested = false, nestedSectionIndex = null, isStandalonePdf = false) => {
//     console.log('🔍 PDF UPLOAD DEBUG: Starting PDF upload', { 
//       sectionIndex, 
//       videoIndex, 
//       isNested, 
//       nestedSectionIndex,
//       isStandalonePdf 
//     });
    
//     let targetObject;
//     let sectionId;
//     let videoId = null;
    
//     if (isStandalonePdf) {
//       if (isNested && nestedSectionIndex !== null) {
//         targetObject = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.pdfs?.[videoIndex];
//         sectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
//       } else {
//         targetObject = courseData.sections?.[sectionIndex]?.pdfs?.[videoIndex];
//         sectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
//       }
//     } else {
//       if (isNested && nestedSectionIndex !== null) {
//         targetObject = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.videos?.[videoIndex];
//         sectionId = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.serverSectionId;
//       } else {
//         targetObject = courseData.sections?.[sectionIndex]?.videos?.[videoIndex];
//         sectionId = courseData.sections?.[sectionIndex]?.serverSectionId;
//       }
//       videoId = targetObject?.serverVideoId;
//     }
    
//     if (!targetObject) {
//       showToast('PDF object not found.', 'error');
//       return;
//     }
    
//     let fileToUpload = null;
//     if (isStandalonePdf) {
//       fileToUpload = targetObject?.file;
//       if (!fileToUpload) {
//         showToast('No PDF file selected for upload.', 'error');
//         return;
//       }
//     } else {
//       fileToUpload = targetObject?.pdfFile;
//       if (!fileToUpload) {
//         showToast('No PDF file selected for video.', 'error');
//         return;
//       }
//     }

//     // ✅ FIX: changed name will be used here
//     const finalTitle = sanitizePdfFileName(
//       targetObject?.title || fileToUpload?.name?.replace(/\.[^/.]+$/, '') || 'document'
//     );

//     const renamedPdfFile = createRenamedPdfFile(fileToUpload, finalTitle);

//     if (isStandalonePdf) {
//       handlePdfInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'uploading', isNested, nestedSectionIndex);
//     } else {
//       if (isNested && nestedSectionIndex !== null) {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'uploading', isNested, nestedSectionIndex);
//       } else {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'uploading', isNested);
//       }
//     }

//     try {
//       const formData = new FormData();

//       // ✅ IMPORTANT FIX
//       formData.append('pdf', renamedPdfFile);
//       formData.append('title', finalTitle);
      
//       if (!isStandalonePdf && videoId) {
//         formData.append('videoId', videoId.toString());
//       } else if (isStandalonePdf && sectionId) {
//         formData.append('sectionId', sectionId.toString());
//       } else {
//         throw new Error('Either videoId or sectionId is required for PDF upload');
//       }
      
//       const result = await uploadPdf(formData).unwrap();

//       const pdfUrl = result.pdf?.fileUrl || result.fileUrl || result.url || result.pdf?.url;
//       const pdfId = result.pdf?.id || result.id;
//       const savedTitle = result.pdf?.title || result.title || finalTitle;
      
//       if (!pdfUrl) {
//         showToast('PDF uploaded but URL not found in response', 'warning');
//         return;
//       }

//       if (isStandalonePdf) {
//         const updatedSections = [...courseData.sections];
        
//         if (isNested && nestedSectionIndex !== null) {
//           updatedSections[sectionIndex] = {
//             ...updatedSections[sectionIndex],
//             nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//               if (idx === nestedSectionIndex) {
//                 const updatedPdfs = [...(nestedSection.pdfs || [])];
//                 if (updatedPdfs[videoIndex]) {
//                   updatedPdfs[videoIndex] = {
//                     ...updatedPdfs[videoIndex],
//                     title: savedTitle,
//                     serverPdfUrl: pdfUrl,
//                     serverPdfId: pdfId,
//                     uploadStatus: 'success',
//                     file: null,
//                     isStandalone: true
//                   };
//                 }
//                 return {
//                   ...nestedSection,
//                   pdfs: updatedPdfs
//                 };
//               }
//               return nestedSection;
//             })
//           };
//         } else {
//           updatedSections[sectionIndex] = {
//             ...updatedSections[sectionIndex],
//             pdfs: updatedSections[sectionIndex].pdfs.map((pdf, idx) => {
//               if (idx === videoIndex) {
//                 return {
//                   ...pdf,
//                   title: savedTitle,
//                   serverPdfUrl: pdfUrl,
//                   serverPdfId: pdfId,
//                   uploadStatus: 'success',
//                   file: null,
//                   isStandalone: true
//                 };
//               }
//               return pdf;
//             })
//           };
//         }
        
//         onInputChange('sections', updatedSections);
//         showToast(`PDF "${savedTitle}" uploaded successfully!`, 'success');
        
//       } else {
//         if (isNested && nestedSectionIndex !== null) {
//           const updatedSections = [...courseData.sections];
          
//           updatedSections[sectionIndex] = {
//             ...updatedSections[sectionIndex],
//             nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//               if (idx === nestedSectionIndex) {
//                 const updatedVideos = [...nestedSection.videos];
//                 if (updatedVideos[videoIndex]) {
//                   updatedVideos[videoIndex] = {
//                     ...updatedVideos[videoIndex],
//                     title: savedTitle,
//                     serverPdfUrl: pdfUrl,
//                     serverPdfId: pdfId,
//                     pdfUploadStatus: 'success',
//                     pdfFile: null,
//                     hasPdf: true
//                   };
//                 }
//                 return {
//                   ...nestedSection,
//                   videos: updatedVideos
//                 };
//               }
//               return nestedSection;
//             })
//           };
          
//           onInputChange('sections', updatedSections);
//         } else {
//           const updatedSections = [...courseData.sections];
          
//           if (updatedSections[sectionIndex]?.videos?.[videoIndex]) {
//             updatedSections[sectionIndex] = {
//               ...updatedSections[sectionIndex],
//               videos: [...updatedSections[sectionIndex].videos]
//             };
            
//             updatedSections[sectionIndex].videos[videoIndex] = {
//               ...updatedSections[sectionIndex].videos[videoIndex],
//               title: savedTitle,
//               serverPdfUrl: pdfUrl,
//               serverPdfId: pdfId,
//               pdfUploadStatus: 'success',
//               pdfFile: null,
//               hasPdf: true
//             };
//           }
          
//           onInputChange('sections', updatedSections);
//         }

//         showToast(`PDF for "${savedTitle}" uploaded successfully!`, 'success');
//       }

//     } catch (error) {
//       console.error('❌ PDF upload failed:', error);
      
//       if (isStandalonePdf) {
//         handlePdfInSectionChange(sectionIndex, videoIndex, 'uploadStatus', 'error', isNested, nestedSectionIndex);
//       } else {
//         if (isNested && nestedSectionIndex !== null) {
//           handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'error', isNested, nestedSectionIndex);
//         } else {
//           handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfUploadStatus', 'error', isNested);
//         }
//       }
      
//       let errorMessage = 'Failed to upload PDF. ';
      
//       if (error.status === 400) {
//         errorMessage = 'Bad Request: Invalid video ID or PDF file format.';
//       } else if (error.status === 401) {
//         errorMessage = 'Authentication failed. Please log in again.';
//       } else if (error.status === 413) {
//         errorMessage = 'File is too large. Maximum size is 50MB.';
//       } else if (error.status === 415) {
//         errorMessage = 'Unsupported file type. Please upload a PDF file.';
//       } else if (error.status === 500) {
//         errorMessage = 'Server error. Please try again later.';
//       } else if (error.message?.includes('network')) {
//         errorMessage = 'Network error. Please check your internet connection.';
//       } else if (error.data?.message) {
//         errorMessage = error.data.message;
//       } else if (error.message) {
//         errorMessage += error.message;
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleDeletePdf = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
//     let video;
//     if (isNested && nestedSectionIndex !== null) {
//       video = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].videos[videoIndex];
//     } else {
//       video = courseData.sections[sectionIndex].videos[videoIndex];
//     }
    
//     if (!video.serverPdfId) {
//       showToast('PDF ID is missing. Cannot delete PDF.', 'error');
//       return;
//     }

//     if (isNested && nestedSectionIndex !== null) {
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', 'deleting', isNested, nestedSectionIndex);
//     } else {
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', 'deleting', isNested);
//     }

//     try {
//       const result = await deletePdf(video.serverPdfId).unwrap();
      
//       if (result.success) {
//         showToast('PDF deleted successfully', 'success');
        
//         if (isNested && nestedSectionIndex !== null) {
//           const updatedSections = [...courseData.sections];
          
//           updatedSections[sectionIndex] = {
//             ...updatedSections[sectionIndex],
//             nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//               if (idx === nestedSectionIndex) {
//                 const updatedVideos = [...nestedSection.videos];
//                 if (updatedVideos[videoIndex]) {
//                   updatedVideos[videoIndex] = {
//                     ...updatedVideos[videoIndex],
//                     pdfFile: null,
//                     serverPdfUrl: null,
//                     serverPdfId: null,
//                     pdfUploadStatus: null,
//                     pdfDeleteStatus: null,
//                     hasPdf: false
//                   };
//                 }
//                 return {
//                   ...nestedSection,
//                   videos: updatedVideos
//                 };
//               }
//               return nestedSection;
//             })
//           };
          
//           onInputChange('sections', updatedSections);
//         } else {
//           const updatedSections = [...courseData.sections];
          
//           if (updatedSections[sectionIndex]?.videos?.[videoIndex]) {
//             updatedSections[sectionIndex] = {
//               ...updatedSections[sectionIndex],
//               videos: [...updatedSections[sectionIndex].videos]
//             };
            
//             updatedSections[sectionIndex].videos[videoIndex] = {
//               ...updatedSections[sectionIndex].videos[videoIndex],
//               pdfFile: null,
//               serverPdfUrl: null,
//               serverPdfId: null,
//               pdfUploadStatus: null,
//               pdfDeleteStatus: null,
//               hasPdf: false
//             };
//           }
          
//           onInputChange('sections', updatedSections);
//         }
//       } else {
//         throw new Error(result.message || 'Failed to delete PDF');
//       }
//     } catch (error) {
//       console.error('❌ Failed to delete PDF:', error);
      
//       if (isNested && nestedSectionIndex !== null) {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', null, isNested, nestedSectionIndex);
//       } else {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'pdfDeleteStatus', null, isNested);
//       }
      
//       let errorMessage = 'Failed to delete PDF. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       }
//       showToast(errorMessage, 'error');
//     }
//   };

//   // ==================== STANDALONE PDF FUNCTIONS ====================

//   const handleStandalonePdfLockToggle = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null, newLockStatus) => {
//     let pdf;
//     if (isNested && nestedSectionIndex !== null) {
//       pdf = courseData.sections?.[sectionIndex]?.nestedSections?.[nestedSectionIndex]?.pdfs?.[pdfIndex];
//     } else {
//       pdf = courseData.sections?.[sectionIndex]?.pdfs?.[pdfIndex];
//     }
    
//     if (!pdf) {
//       showToast('PDF not found', 'error');
//       return;
//     }
    
//     if (!pdf.serverPdfId) {
//       const updatedSections = [...courseData.sections];
      
//       if (isNested && nestedSectionIndex !== null) {
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//             if (idx === nestedSectionIndex) {
//               const updatedPdfs = [...(nestedSec.pdfs || [])];
//               if (updatedPdfs[pdfIndex]) {
//                 updatedPdfs[pdfIndex] = {
//                   ...updatedPdfs[pdfIndex],
//                   isLocked: newLockStatus
//                 };
//               }
//               return {
//                 ...nestedSec,
//                 pdfs: updatedPdfs
//               };
//             }
//             return nestedSec;
//           })
//         };
//       } else {
//         updatedSections[sectionIndex] = {
//           ...updatedSections[sectionIndex],
//           pdfs: updatedSections[sectionIndex].pdfs.map((p, idx) => 
//             idx === pdfIndex ? { ...p, isLocked: newLockStatus } : p
//           )
//         };
//       }
      
//       onInputChange('sections', updatedSections);
//       showToast(`PDF lock status set to ${newLockStatus ? 'locked' : 'unlocked'} (will apply after upload)`, 'info');
//       return;
//     }

//     const updatedSections = [...courseData.sections];
  
//     if (isNested && nestedSectionIndex !== null) {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//           if (idx === nestedSectionIndex) {
//             const updatedPdfs = [...(nestedSec.pdfs || [])];
//             if (updatedPdfs[pdfIndex]) {
//               updatedPdfs[pdfIndex] = {
//                 ...updatedPdfs[pdfIndex],
//                 isLocked: newLockStatus,
//                 lockUpdateStatus: 'updating'
//               };
//             }
//             return {
//               ...nestedSec,
//               pdfs: updatedPdfs
//             };
//           }
//           return nestedSec;
//         })
//       };
//     } else {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         pdfs: updatedSections[sectionIndex].pdfs.map((p, idx) => 
//           idx === pdfIndex ? { ...p, isLocked: newLockStatus, lockUpdateStatus: 'updating' } : p
//         )
//       };
//     }
  
//     onInputChange('sections', updatedSections);
  
//     try {
//       await updateCourseFeatures({
//         courseId: parseInt(courseData.id, 10),
//         videoIds: [],
//         pdfIds: [{
//           id: parseInt(pdf.serverPdfId, 10),
//           islock: newLockStatus
//         }],
//         quizIds: []
//       }).unwrap();
    
//       const finalSections = [...courseData.sections];
    
//       if (isNested && nestedSectionIndex !== null) {
//         finalSections[sectionIndex] = {
//           ...finalSections[sectionIndex],
//           nestedSections: finalSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//             if (idx === nestedSectionIndex) {
//               const updatedPdfs = [...(nestedSec.pdfs || [])];
//               if (updatedPdfs[pdfIndex]) {
//                 updatedPdfs[pdfIndex] = {
//                   ...updatedPdfs[pdfIndex],
//                   isLocked: newLockStatus,
//                   lockUpdateStatus: null
//                 };
//               }
//               return {
//                 ...nestedSec,
//                 pdfs: updatedPdfs
//               };
//             }
//             return nestedSec;
//           })
//         };
//       } else {
//         finalSections[sectionIndex] = {
//           ...finalSections[sectionIndex],
//           pdfs: finalSections[sectionIndex].pdfs.map((p, idx) => 
//             idx === pdfIndex ? { ...p, isLocked: newLockStatus, lockUpdateStatus: null } : p
//           )
//         };
//       }
    
//       onInputChange('sections', finalSections);
    
//       if (refetchCourse && typeof refetchCourse === 'function') {
//         try {
//           await refetchCourse();
//         } catch (refetchError) {
//           console.log('⚠️ Could not refetch course (expected in create mode):', refetchError.message);
//         }
//       }
    
//       showToast(`PDF "${pdf.title}" ${newLockStatus ? 'locked' : 'unlocked'} successfully`, 'success');
    
//     } catch (error) {
//       console.error('❌ Failed to update standalone PDF lock status:', error);
    
//       const revertSections = [...courseData.sections];
    
//       if (isNested && nestedSectionIndex !== null) {
//         revertSections[sectionIndex] = {
//           ...revertSections[sectionIndex],
//           nestedSections: revertSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//             if (idx === nestedSectionIndex) {
//               const updatedPdfs = [...(nestedSec.pdfs || [])];
//               if (updatedPdfs[pdfIndex]) {
//                 updatedPdfs[pdfIndex] = {
//                   ...updatedPdfs[pdfIndex],
//                   isLocked: !newLockStatus,
//                   lockUpdateStatus: null
//                 };
//               }
//               return {
//                 ...nestedSec,
//                 pdfs: updatedPdfs
//               };
//             }
//             return nestedSec;
//           })
//         };
//       } else {
//         revertSections[sectionIndex] = {
//           ...revertSections[sectionIndex],
//           pdfs: revertSections[sectionIndex].pdfs.map((p, idx) => 
//             idx === pdfIndex ? { ...p, isLocked: !newLockStatus, lockUpdateStatus: null } : p
//           )
//         };
//       }
    
//       onInputChange('sections', revertSections);
    
//       let errorMessage = 'Failed to update PDF lock status. ';
//       if (error.data?.message) {
//         errorMessage += error.data.message;
//       } else if (error.message) {
//         errorMessage += error.message;
//       } else {
//         errorMessage += 'Please try again.';
//       }
    
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleAddPdfToSection = (sectionIndex, file, isNested = false, nestedSectionIndex = null) => {
//     const maxSize = 50 * 1024 * 1024;
//     if (file.size > maxSize) {
//       showToast('PDF file size must be less than 50MB', 'error');
//       return;
//     }

//     if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
//       showToast('Please upload a valid PDF file', 'error');
//       return;
//     }

//     const newPdf = {
//       id: Date.now().toString(),
//       file: file,
//       title: file.name.replace(/\.[^/.]+$/, ""),
//       uploadStatus: null,
//       serverPdfUrl: null,
//       serverPdfId: null,
//       isLocked: true,
//       deleteStatus: null,
//       isStandalone: true
//     };
    
//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//           if (idx === nestedSectionIndex) {
//             return {
//               ...nestedSection,
//               pdfs: [...(nestedSection.pdfs || []), newPdf]
//             };
//           }
//           return nestedSection;
//         })
//       };
//     } else {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         pdfs: [...(updatedSections[sectionIndex].pdfs || []), newPdf]
//       };
//     }
    
//     onInputChange('sections', updatedSections);
//     showToast(`PDF "${file.name}" added to ${isNested ? 'nested ' : ''}section`, 'info');
//   };

//   const handleRemovePdfFromSection = (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       const nestedSection = updatedSections[sectionIndex].nestedSections[nestedSectionIndex];
//       const pdf = nestedSection.pdfs[pdfIndex];
      
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSec, idx) => {
//           if (idx === nestedSectionIndex) {
//             return {
//               ...nestedSec,
//               pdfs: nestedSec.pdfs.filter((_, index) => index !== pdfIndex)
//             };
//           }
//           return nestedSec;
//         })
//       };
      
//       onInputChange('sections', updatedSections);
//       showToast(`PDF "${pdf.title || pdf.file?.name}" removed from nested section`, 'info');
//     } else {
//       const pdf = updatedSections[sectionIndex].pdfs[pdfIndex];
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         pdfs: updatedSections[sectionIndex].pdfs.filter((_, index) => index !== pdfIndex)
//       };
      
//       onInputChange('sections', updatedSections);
//       showToast(`PDF "${pdf.title || pdf.file?.name}" removed from section`, 'info');
//     }
//   };

//   const handlePdfInSectionChange = (sectionIndex, pdfIndex, field, value, isNested = false, nestedSectionIndex = null) => {
//     const updatedSections = [...courseData.sections];
    
//     if (isNested && nestedSectionIndex !== null) {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         nestedSections: updatedSections[sectionIndex].nestedSections.map((nestedSection, idx) => {
//           if (idx === nestedSectionIndex) {
//             const updatedPdfs = [...(nestedSection.pdfs || [])];
//             updatedPdfs[pdfIndex] = {
//               ...updatedPdfs[pdfIndex],
//               [field]: value
//             };
//             return {
//               ...nestedSection,
//               pdfs: updatedPdfs
//             };
//           }
//           return nestedSection;
//         })
//       };
//     } else {
//       updatedSections[sectionIndex] = {
//         ...updatedSections[sectionIndex],
//         pdfs: [...(updatedSections[sectionIndex].pdfs || [])]
//       };
      
//       updatedSections[sectionIndex].pdfs[pdfIndex] = {
//         ...updatedSections[sectionIndex].pdfs[pdfIndex],
//         [field]: value
//       };
//     }
    
//     onInputChange('sections', updatedSections);
//   };

//   const handleUploadStandalonePdfToServer = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
//     await handleUploadPdfToServer(
//       sectionIndex, 
//       pdfIndex, 
//       isNested, 
//       nestedSectionIndex, 
//       true
//     );
//   };

//   const handleDeleteStandalonePdf = async (sectionIndex, pdfIndex, isNested = false, nestedSectionIndex = null) => {
//     let pdf;
//     if (isNested && nestedSectionIndex !== null) {
//       pdf = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].pdfs[pdfIndex];
//     } else {
//       pdf = courseData.sections[sectionIndex].pdfs[pdfIndex];
//     }
    
//     if (!pdf.serverPdfId) {
//       showToast('PDF ID is missing. Cannot delete PDF.', 'error');
//       return;
//     }

//     if (!window.confirm(`Are you sure you want to delete the PDF "${pdf.title}"?`)) {
//       return;
//     }

//     handlePdfInSectionChange(sectionIndex, pdfIndex, 'deleteStatus', 'deleting', isNested, nestedSectionIndex);

//     try {
//       const result = await deleteStandalonePdf(pdf.serverPdfId).unwrap();
      
//       if (result.success) {
//         showToast('PDF deleted successfully', 'success');
//         handleRemovePdfFromSection(sectionIndex, pdfIndex, isNested, nestedSectionIndex);
//       } else {
//         throw new Error(result.message || 'Failed to delete PDF');
//       }
//     } catch (error) {
//       console.error('❌ Failed to delete standalone PDF:', error);
      
//       handlePdfInSectionChange(sectionIndex, pdfIndex, 'deleteStatus', null, isNested, nestedSectionIndex);
      
//       let errorMessage = 'Failed to delete PDF. ';
//       if (error.data?.message) {
//         errorMessage += error.data.message;
//       } else if (error.status === 401) {
//         errorMessage += 'Authentication failed. Please log in again.';
//       } else if (error.status === 403) {
//         errorMessage += 'You do not have permission to delete this PDF.';
//       } else if (error.status === 404) {
//         errorMessage += 'PDF not found. It may have been already deleted.';
//       } else if (error.status === 500) {
//         errorMessage += 'Server error. Please try again later.';
//       } else {
//         errorMessage += 'Please try again.';
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleDeleteVideo = async (sectionIndex, videoIndex, isNested = false, nestedSectionIndex = null) => {
//     let video;
//     if (isNested && nestedSectionIndex !== null) {
//       video = courseData.sections[sectionIndex].nestedSections[nestedSectionIndex].videos[videoIndex];
//     } else {
//       video = courseData.sections[sectionIndex].videos[videoIndex];
//     }
    
//     if (!video.serverVideoId) {
//       showToast('Video ID is missing. Cannot delete video.', 'error');
//       return;
//     }

//     if (isNested && nestedSectionIndex !== null) {
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', 'deleting', isNested, nestedSectionIndex);
//     } else {
//       handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', 'deleting', isNested);
//     }

//     try {
//       const result = await deleteVideo(video.serverVideoId).unwrap();
      
//       if (result.success) {
//         showToast('Video deleted successfully', 'success');
//         handleRemoveVideoFromSection(sectionIndex, videoIndex, isNested, nestedSectionIndex);
//       } else {
//         throw new Error(result.message || 'Failed to delete video');
//       }

//     } catch (error) {
//       console.error('❌ Failed to delete video:', error);
      
//       if (isNested && nestedSectionIndex !== null) {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', null, isNested, nestedSectionIndex);
//       } else {
//         handleVideoInSectionChange(sectionIndex, videoIndex, 'deleteStatus', null, isNested);
//       }
      
//       let errorMessage = 'Failed to delete video. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleDeleteSection = async (sectionIndex, isNested = false) => {
//     const section = courseData.sections[sectionIndex];
    
//     if (!section.serverSectionId) {
//       showToast('Section ID is missing. Cannot delete section.', 'error');
//       return;
//     }

//     const updatedSections = [...courseData.sections];
//     updatedSections[sectionIndex] = {
//       ...updatedSections[sectionIndex],
//       deleteStatus: 'deleting'
//     };
//     onInputChange('sections', updatedSections);

//     try {
//       const result = await deleteSection(section.serverSectionId).unwrap();
      
//       if (result.success) {
//         showToast('Section deleted successfully', 'success');
//         const filteredSections = courseData.sections.filter((_, index) => index !== sectionIndex);
//         onInputChange('sections', filteredSections);
//       } else {
//         throw new Error(result.message || 'Failed to delete section');
//       }

//     } catch (error) {
//       console.error('❌ Failed to delete section:', error);
      
//       const resetSections = [...courseData.sections];
//       resetSections[sectionIndex] = {
//         ...resetSections[sectionIndex],
//         deleteStatus: null
//       };
//       onInputChange('sections', resetSections);
      
//       let errorMessage = 'Failed to delete section. Please try again.';
//       if (error.data?.message) {
//         errorMessage = error.data.message;
//       }
      
//       showToast(errorMessage, 'error');
//     }
//   };

//   const handleRemoveSection = (sectionIndex) => {
//     const section = courseData.sections[sectionIndex];
//     const filteredSections = courseData.sections.filter((_, index) => index !== sectionIndex);
//     onInputChange('sections', filteredSections);
//     showToast(`Section "${section.name}" removed`, 'info');
//   };

//   return (
//     <div className="p-8 animate-fade-in">
//       {toast && (
//         <Toast 
//           message={toast.message} 
//           type={toast.type} 
//           onClose={() => setToast(null)} 
//         />
//       )}
      
//       <h2 className="text-2xl font-bold text-gray-900 mb-6">Media & Pricing</h2>
      
//       <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
//         <div className="flex items-start">
//           <div className="flex-shrink-0">
//             <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
//               <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
//             </svg>
//           </div>
//           <div className="ml-3">
//             <p className="text-sm text-blue-700">
//               <span className="font-bold">Video Security Enabled:</span> All uploaded videos are restricted to play only on <strong>pengravity.com</strong> and all its subdomains (<strong>*.pengravity.com</strong>). Videos cannot be embedded on any other website.
//             </p>
//             <p className="text-xs text-blue-600 mt-1">
//               ✅ Student panel, staging, and all subdomains are now supported.
//             </p>
//           </div>
//         </div>
//       </div>
      
//       <div className="space-y-8">
//         <FileUploadSection
//           title="Course Thumbnail"
//           field="thumbnail"
//           description="Upload course thumbnail"
//           format="Recommended: 1280x720 pixels"
//           required={true}
//           courseData={courseData}
//           onThumbnailChange={handleThumbnailChange}
//           showToast={showToast}
//         />

//         <div>
//           <div className="flex justify-between items-center mb-6">
//             <label className="block text-sm font-semibold text-gray-700">
//               Course Content *
//             </label>
            
//             <button
//               type="button"
//               onClick={() => setShowSectionForm(true)}
//               className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold transition-all duration-300 hover:from-green-600 hover:to-emerald-700 hover:scale-105"
//             >
//               + Add Section
//             </button>
//           </div>

//           {showSectionForm && (
//             <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
//               <div className="flex items-center space-x-3">
//                 <input
//                   type="text"
//                   value={newSectionName}
//                   onChange={(e) => setNewSectionName(e.target.value)}
//                   placeholder="Enter section name (e.g., 'Introduction', 'Advanced Topics')"
//                   className="flex-1 px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
//                 />
//                 <button
//                   type="button"
//                   onClick={handleAddSection}
//                   className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors duration-300"
//                 >
//                   Create
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => {
//                     setShowSectionForm(false);
//                     setNewSectionName('');
//                   }}
//                   className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
//                 >
//                   Cancel
//                 </button>
//               </div>
//             </div>
//           )}

//           {courseData.sections && courseData.sections.map((section, sectionIndex) => (
//             <div key={section.id}>
//               <SectionComponent
//                 section={section}
//                 sectionIndex={sectionIndex}
//                 handleAddVideoToSection={handleAddVideoToSection}
//                 handleAddPdfToSection={handleAddPdfToSection}
//                 handleRemoveSection={handleRemoveSection}
//                 handleVideoInSectionChange={handleVideoInSectionChange}
//                 handlePdfInSectionChange={handlePdfInSectionChange}
//                 calculateSectionDuration={calculateSectionDuration}
//                 handleUploadVideoToServer={handleUploadVideoToServer}
//                 handleUploadPdfToServer={handleUploadPdfToServer}
//                 handleUploadStandalonePdfToServer={handleUploadStandalonePdfToServer}
//                 handleDeleteSection={handleDeleteSection}
//                 handleDeleteVideo={handleDeleteVideo}
//                 handleDeletePdf={handleDeletePdf}
//                 handleDeleteStandalonePdf={handleDeleteStandalonePdf}
//                 handleRemoveVideoFromSection={handleRemoveVideoFromSection}
//                 handleRemovePdfFromSection={handleRemovePdfFromSection}
//                 showToast={showToast}
//                 handleAddNestedSection={handleAddNestedSection}
//                 handleRemoveNestedSection={handleRemoveNestedSection}
//                 handleViewPdf={handleViewPdf}
//                 handleSectionLockChange={handleSectionLockToggle}
//                 handleStandalonePdfLockChange={handleStandalonePdfLockToggle}
//                 handleAddQuizToSection={(sectionId, quizData) => {
//                   console.log('Adding quiz to section:', { sectionId, quizData });
//                   showToast('Quiz added to section (Demo Mode)', 'success');
//                 }}
//                 courseData={courseData}
//                 refetchCourse={refetchCourse}
//               />
//             </div>
//           ))}

//           {(!courseData.sections || courseData.sections.length === 0) && (
//             <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-2xl">
//               <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
//               </svg>
//               <p className="text-sm mb-4">No sections or content added yet.</p>
//               <p className="text-xs text-gray-400">Create sections to organize your course content.</p>
//             </div>
//           )}
//         </div>

//         <div className="border-t border-gray-200 pt-8">
//           <h3 className="text-xl font-bold text-gray-900 mb-6">Pricing</h3>
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <div>
//               <label className="block text-sm font-semibold text-gray-700 mb-2">
//                 Course Price *
//               </label>
//               <div className="relative">
//                 <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
//                 <input
//                   type="number"
//                   value={courseData.price}
//                   onChange={(e) => onInputChange('price', e.target.value)}
//                   placeholder="0.00"
//                   min="0"
//                   step="0.01"
//                   className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
//                   required
//                 />
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-semibold text-gray-700 mb-2">
//                 Promotional Price
//               </label>
//               <div className="relative">
//                 <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
//                 <input
//                   type="number"
//                   value={courseData.promoPrice}
//                   onChange={(e) => onInputChange('promoPrice', e.target.value)}
//                   placeholder="0.00"
//                   min="0"
//                   step="0.01"
//                   className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MediaPricingStep;